import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ParentOverviewScreen } from "./ParentOverviewScreen";
import {
  CheckIcon,
  CheckboxRow,
  ChildrenIcon,
  DashButton,
  DataSurface,
  DownloadIcon,
  GamesPanel,
  InfoIcon,
  Note,
  PageHead,
  Panel,
  PlayIcon,
  PrivacyIcon,
  SectionHead,
  StatusPill,
  StickerIcon,
  TrendUpIcon,
  FIELD,
  /* The de-emoji set, plus the one skill→colour map §3 fixes. */
  AlertIcon,
  CalendarIcon,
  SearchIcon,
  SparkleIcon,
  VoiceIcon,
  skillTint,
} from "../../../shared/components";
import { useResource, type Resource } from "../../../shared/hooks/useResource";
/* ⚠️ Deep imports, matching `SkillProgress`: `../curriculum` re-exports all five
   content tables, and this screen has no reason to ship a word list to render
   "Level 2 of 3". */
import { formatLevel } from "../../../shared/lib/format";
import { LEVEL_COUNT } from "../../../shared/curriculum/progress";
import { getThread, type MessageThread } from "../../../shared/api/messages";

/**
 * The window C1's overview reads.
 *
 * ⚠️ SHORTER THAN C2's 28. C1 is specified as *"a 30-second read on a phone"* —
 * this week's games, this week's minutes, the last few things played — and a
 * month of days behind those numbers makes "Learning Time" answer a question
 * nobody asked on this screen. C2 is where the longer window belongs.
 */
const OVERVIEW_WINDOW_DAYS = 7;
import {
  getReduceMotionSetting,
  setReduceMotionSetting,
} from "../../../shared/hooks/useReduceMotion";
import {
  getActivity,
  getAttendance,
  getChildInsight,
  getChildPreferences,
  getChildProgress,
  getChildren,
  getAccountPreferences,
  getDashboard,
  getNotifications,
  saveAccountPreferences,
  saveChildPreferences,
  type AccountPreferences,
  type ChildPreferences,
  type ChildSummary,
  type Insight,
  type ParentActivity,
  type AttendanceState,
  type ParentAttendance,
  type ParentAuth,
  type ParentDashboard,
  type ParentNotification,
  type ProgressSnapshot,
} from "../../../shared/api/parent";
import {
  getPlayerGames,
  type GameStatsResponse,
} from "../../../shared/api/games";
import { GAMES_PANEL_COPY } from "../../../shared/ui/gamesCopy";
import { FeedbackModal } from "../../feedback";
import {
  getParentAnnouncements,
  type ParentAnnouncement,
} from "../../../shared/api/announcements";
import {
  mergeAnnouncements,
  useParentEvents,
} from "../../../shared/hooks/useParentEvents";
import {
  downloadExport,
  eraseChildData,
  exportChildData,
  getConsent,
  getConsentNotice,
  grantConsent,
  revokeConsent,
  type ConsentView,
  type ErasureResult,
} from "../../../shared/api/rights";
import {
  PARENT_COPY,
  consentLabel,
  consentTone,
  describeError,
  type Lang,
} from "./copy";
import {
  attendanceCalendar,
  attendanceMonth,
  dayKey,
  formatDay,
  monthsInWindow,
  shiftDays,
  type CalendarDay,
} from "./attendanceCalendar";
import {
  AttendanceMonthGrid,
} from "./PlayfulUI";
import { contentPercent, contentStatus, skillSections } from "./skillSections";
import {
  PARENT_VERIFICATION,
  mayWriteConsent,
  retentionIntent,
} from "./consentIntent";
import { RewardsPanel } from "./Rewards";
import { DayDetail, istToday, skillLabel } from "./DayViews";
import {
  AccuracyRing,
  DayBars,
  MetricCard,
  SkillBars,
  WeekdayBars,
} from "./Charts";
import {
  accuracy,
  busiestDay,
  consistency,
  dayIndex,
  perActiveDay,
  series,
  skillShares,
  weekdays,
} from "./activityModel";
import { ReportSheet } from "./ReportSheet";

/**
 * C1–C5, plus the consent screen contract D1 requires.
 *
 * 🔴 EVERY SCREEN ISSUES ITS OWN REAL REQUEST. None takes data as a prop and
 * there is no fixture file in this directory — §8.4's TODO row is *"the
 * predecessor rendered 8 hardcoded children while working endpoints sat
 * uncalled"*, and a component taking `rows` with a fixture beside it is that
 * failure one level up.
 *
 * ✅ AND UNLIKE THE TEACHER TIER, THESE ACTUALLY LOAD. Parent login exists
 * (§9.1), so the credential these screens need is real. The teacher tier's 401
 * is a missing route; a 401 here is a 15-minute token that aged out with no
 * refresh to rotate it, which is why `describeError` says something different.
 */

interface Base {
  lang?: Lang;
  auth?: ParentAuth;
}

/** The four states, wired to `DataSurface`'s contract in one place. */
function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  emptyActionLabel,
  onEmptyAction,
  skeletonRows = 4,
}: {
  resource: Resource<T>;
  lang: Lang;
  children: (data: T) => ReactNode;
  emptyMessage?: ReactNode;
  emptyActionLabel?: ReactNode;
  onEmptyAction?: () => void;
  skeletonRows?: number;
}) {
  const t = PARENT_COPY[lang];
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.empty}
      emptyActionLabel={emptyActionLabel}
      onEmptyAction={onEmptyAction}
      errorMessage={describeError(resource.error, lang)}
      /* 🔴 "When it last worked" — DataSurface calls this the clause that gets
         dropped, and the one that tells a reader whether to panic. */
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  );
}

/**
 * The two grouped skill sections — C1 and C2 draw the identical block.
 *
 * 🔴 `SkillProgress` DECIDES THE SHAPE, NOT THIS COMPONENT. It has no `variant`
 * prop and derives the family from the skill, so a content skill cannot render
 * as a percentage even by accident. All this does is supply the numbers and the
 * caption that carries the denominator.
 *
 * ✅ AND THE CONTENT HALF IS NOW FIVE GAMES RATHER THAN FIVE BARS. The five
 * content skills are one-to-one with the five games (design-system.md §3), and
 * the plate a child taps to start each one already exists in `public/images` —
 * so the section a parent looks at first was the only place in the product that
 * knew *"Numeracy, level 2"* without knowing *"Sky Quest"*. `LearningCard`
 * supplies the picture, the game's name and the skill's fixed colour; the number
 * inside it is still drawn by `SkillProgress`, unchanged, because a card that
 * hand-drew its own segments would be that structural guarantee quietly traded
 * for a nicer-looking box.
 *
 * ⚠️ THE MOTOR HALF KEEPS ITS PLAIN LIST ON PURPOSE. A motor skill is earned
 * across all five games, so there is no plate that is honestly its picture —
 * giving it one would tell a parent their child's balance comes from Letter
 * Lagoon alone. The two families look different because they *are* different,
 * which is the reason §3 splits them at all.
 */
function SkillSections({
  dash,
  lang,
  breakdownOpen = false,
  onToggleBreakdown,
}: {
  dash: ParentDashboard;
  lang: Lang;
  /** Whether the numbers panel below this one is showing. */
  breakdownOpen?: boolean;
  /**
   * ✅ THE "View detailed breakdown" BUTTON HAS SOMETHING TO OPEN NOW.
   *
   * 🔴 IT WAS A `<button type="button">` WITH NO HANDLER, and what it promised
   * had been built and rendered by nobody: `ProgressNumbers` — this child's real
   * day counts, minutes, answers and weekday pattern from `GET …/activity` —
   * was exported from this file and had no caller anywhere in the product. A
   * dead control over a panel that exists is the worst version of this: the
   * feature shipped, and the only door to it did nothing.
   */
  onToggleBreakdown?: () => void;
}) {
  const t = PARENT_COPY[lang];
  const sections = useMemo(() => skillSections(dash, lang), [dash, lang]);
  /* ⚠️ EMPTY UNTIL A NAME ARRIVES, and the copy below drops the name rather
     than addressing a blank — `ParentOverviewScreen` holds the same rule. */
  const firstName = dash.childName.split(" ")[0] ?? "";

  /*
   * The short mnemonic in each skill disc.
   *
   * 🔴 IT WAS TWO TEXT LABELS AND THREE EMOJI — "123", "ABC", then a globe, a
   * pair of hands and a triangle. So two discs were legible on every machine
   * and three were a font-stack lottery, in one column, at three different
   * optical weights. Five short words is the version that renders the same
   * everywhere and reads at a glance.
   *
   * ⚠️ THE COLOURS ARE GONE FROM THIS TABLE, NOT RECOLOURED. They were five
   * Tailwind pairs (`bg-purple-100 text-purple-700`, `bg-emerald-100 …`) that
   * duplicated the skill→colour map §3 already fixes; the disc now takes
   * `skillTint`, which is the same colour the child sees for that skill and
   * the same one the meter below it draws.
   */
  const domainLabel: Record<string, string> = {
    NUMERACY: "123",
    LITERACY: "ABC",
    GENERAL_AWARENESS: "World",
    SOCIAL_AWARENESS: "Us",
    MATCHING: "Match",
  };

  const domainSubtitles: Record<string, string> = {
    NUMERACY: "Counting, comparing, patterns",
    LITERACY: "Phonics, vocabulary, spelling",
    GENERAL_AWARENESS: "People, places, environment",
    SOCIAL_AWARENESS: "Sharing, helping, respecting",
    MATCHING: "Shapes, sizes, classification",
  };

  /**
   * 🔴 THE STATUS COMES FROM THE CHILD'S LEVEL, NOT FROM WHICH SUBJECT IT IS.
   *
   * This was a map keyed by DOMAIN: Numeracy always read "In Progress", Matching
   * always "Not Started" — for every child on the platform, whatever they had
   * done. A child who had finished all three Matching levels was shown "Not
   * Started" to their own parent, and a child who had never opened Numeracy was
   * shown "In Progress".
   *
   * ⚠️ THE COLOUR STAYS PER DOMAIN. That is decoration and carries no claim; the
   * WORD is the claim, and it now comes from `level`.
   */
  const domainTint: Record<string, { bg: string; text: string }> = {
    NUMERACY: { bg: "bg-purple-50 border-purple-200", text: "text-purple-700" },
    LITERACY: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" },
    GENERAL_AWARENESS: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700" },
    SOCIAL_AWARENESS: { bg: "bg-rose-50 border-rose-200", text: "text-rose-700" },
    MATCHING: { bg: "bg-slate-100 border-slate-200", text: "text-slate-600" },
  };

  /* ⚠️ `contentStatus` AND `contentPercent` LIVE IN `skillSections.ts`, beside
     `motorPercent`, because this repo has no DOM renderer (OD-60) — arithmetic
     left inline in JSX is arithmetic with no test, which is exactly how the
     invented bar below got here. */

  /*
   * 🔴 THIS TABLE USED TO CARRY A `tip` AND A `tipIcon` PER SKILL, AND THEY
   * WERE FIXED SENTENCES OF PRAISE WITH NO CONNECTION TO THE CHILD.
   *
   * `COORDINATION` always said "Good progress! Keep it up." `BALANCE` always
   * said "Improving steadily." `BODY_AWARENESS` always said "Keep practicing!"
   * — printed under a bar whatever the numbers were, and printed for a child
   * who had never played at all. "Improving steadily" is a claim about a
   * trajectory this product does not measure, made about somebody's
   * four-year-old, in the app their parent checks.
   *
   * There is no honest version of that sentence available here, so there is no
   * sentence. `caption` — "Exercised in 8 of 10 sessions", built by
   * `skillSections` — is the real evidence and is already drawn under the bar.
   *
   * ✅ AND THE COLOURS ARE THE SKILL TOKENS NOW. Each row carried `iconBg`
   * and `iconColor` as Tailwind defaults (`bg-purple-100 text-purple-600`,
   * `bg-emerald-100`, `bg-amber-100`, `bg-rose-100`), and the bar underneath
   * picked a fifth set (`bg-purple-500`, `bg-emerald-500`…). §3 fixes one
   * colour per skill so a parent recognises balance by its teal everywhere;
   * `skillTint` is that map and is what the child's own screens use.
   */
  const motorMeta: Record<string, { subtitle: string }> = {
    COORDINATION: { subtitle: "Hand-eye coordination and control" },
    BALANCE: { subtitle: "Body balance and posture" },
    BODY_AWARENESS: { subtitle: "Understanding body and movement" },
    ACTIVE_PLAY: { subtitle: "Energy, movement and activity" },
  };

  return (
    <div className="space-y-8 mt-4">
      {/* ── Section 1: What they are learning ── */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-dash text-dash-lg font-black text-ink flex items-center gap-2">
                What they are learning
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-500 cursor-help" title={t.contentSkillsHint}>
                  i
                </span>
              </h2>
            </div>
            <p className="font-dash text-dash-xs text-muted">
              One game teaches each of these. The progress shows what your child taps.
            </p>
          </div>
          <span className="rounded-full bg-pink-50 border border-pink-100 px-3 py-1 font-dash text-dash-xs font-bold text-pink-600">
            {sections.content.length} Domains
          </span>
        </div>

        {/* 5 Domains Single Unified Card */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-xs space-y-4">
          <div className="divide-y divide-slate-100">
            {sections.content.map((s) => {
              const domainMnemonic = domainLabel[s.skill] ?? s.label.slice(0, 3);
              const sub = domainSubtitles[s.skill] || "";
              const tint =
                domainTint[s.skill] || { bg: "bg-slate-100 border-slate-200", text: "text-slate-600" };
              const pill = { ...tint, label: contentStatus(s.level) };
              /*
               * 🔴 THE BAR IS THE LEVEL, AND NOTHING ELSE. This read
               * `… : s.skill === "GENERAL_AWARENESS" ? 20 : s.skill === "SOCIAL_AWARENESS" ? 10 : 0` —
               * so a child at level 0, who has done nothing at all, was drawn
               * with a fifth of a General Awareness bar filled in. That is
               * invented progress about a named child, shown to their parent.
               *
               * ⚠️ IT ALSO NOW AGREES WITH THE LABEL BESIDE IT. The old ladder
               * put a 40% bar next to the words "Level 1 of 3", and
               * `design-system.md` §8's rule is that sibling values must not be
               * able to contradict each other.
               */
              const pct = contentPercent(s.level);
              const barColor = s.skill === "NUMERACY" ? "bg-purple-500" : s.skill === "LITERACY" ? "bg-emerald-500" : s.skill === "GENERAL_AWARENESS" ? "bg-amber-400" : s.skill === "SOCIAL_AWARENESS" ? "bg-rose-400" : "bg-slate-300";

              return (
                <div key={s.skill} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3.5 min-w-[200px]">
                    <span
                      aria-hidden="true"
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-dash text-dash-xs font-extrabold shadow-2xs"
                      style={{
                        background: `color-mix(in srgb, ${skillTint(s.skill)} 15%, var(--color-surface))`,
                        color: `color-mix(in srgb, ${skillTint(s.skill)} 68%, var(--color-ink))`,
                      }}
                    >
                      {domainMnemonic}
                    </span>
                    <div>
                      <h4 className="font-dash text-dash-sm font-extrabold text-ink">{s.label}</h4>
                      <p className="font-dash text-dash-xs text-muted">{sub}</p>
                    </div>
                  </div>

                  {/* Level & Progress Bar */}
                  <div className="flex-1 max-w-xs space-y-1.5">
                    <div className="flex items-center justify-between text-dash-xs font-bold">
                      {/* 🔴 `formatLevel`, NOT `t.levelTemplate(…)`. `levelTemplate` is a
                          STRING with `{level}` and `{of}` placeholders — every other
                          caller in the product passes it to `formatLevel`
                          (`SkillProgress`, `GamesPanel`, `ReportSheet`). Calling it
                          threw `t.levelTemplate is not a function` on render, which
                          took the whole tree down: React unmounts on a render throw,
                          so the Reports screen went blank rather than showing an
                          error anybody could act on. */}
                      <span className="text-slate-600">
                        {formatLevel(s.level, LEVEL_COUNT, t.levelTemplate)}
                      </span>
                      <span className="text-slate-400 tabular">{pct}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  {/* Status Pill */}
                  <div className="shrink-0 flex items-center">
                    <span className={`inline-block rounded-full border px-3 py-1 font-dash text-[11px] font-extrabold ${pill.bg} ${pill.text}`}>
                      {pill.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {onToggleBreakdown ? (
            <div className="pt-2 border-t border-slate-100 text-center">
              <button
                type="button"
                aria-expanded={breakdownOpen}
                onClick={onToggleBreakdown}
                className="font-dash text-dash-xs font-bold text-pink-600 hover:text-pink-700 transition-colors inline-flex items-center -my-3.5 py-3.5 gap-1 cursor-pointer"
              >
                {breakdownOpen ? "Hide detailed breakdown ↑" : "View detailed breakdown →"}
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {/* ── Section 2: How they are moving ── */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-dash text-dash-lg font-black text-ink flex items-center gap-2">
                How they are moving
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-500 cursor-help" title={t.motorSkillsHint}>
                  i
                </span>
              </h2>
            </div>
            <p className="font-dash text-dash-xs text-muted">
              Earned across all five domains, not taught by one.
            </p>
          </div>
          {/*
            ✅ THE "Last 28 days" CHIP IS GONE, AND DELETING IT IS THE FIX RATHER
            THAN WIRING IT UP — the same call `ParentOverviewScreen` records for
            the "This week / Last week" select it removed.

            🔴 IT COULD NOT HAVE BEEN WIRED HERE. These cards read
            `GET /parent/children/:id/dashboard`, a route that takes no window
            and answers with cumulative facts: `motor` is "exercised in 8 of 10
            sittings" over everything on record. Adding a `days` parameter to
            make a chip work would mean inventing a windowed reading of a
            cumulative number, which is the failure `skillSections` exists to
            prevent. The parent tier's real window is on `activity`, and the
            breakdown button above is the door to it.
          */}
        </div>

        {/*
          The motor-skill cards.

          🔴 THIS GRID INVENTED A PERCENTAGE FOR A CHILD WITH NO DATA, AND IT
          IS THE WORST THING IN THE PARENT TIER.

          The line was:

              const pct = s.percent > 0 ? s.percent
                : s.skill === 'COORDINATION' ? 65
                : s.skill === 'BALANCE' ? 48
                : s.skill === 'BODY_AWARENESS' ? 52 : 40

          So when a child had no recorded movement at all — a new starter, a
          child whose sittings all ran unrecorded, a family whose consent has
          not been given so nothing is kept — the screen drew a filled bar at
          65%, 48%, 52% or 40% and printed it as that child's coordination.
          Four numbers, chosen to look plausible, shown to a parent as a
          measurement of their four-year-old.

          `skillSections` already computes the honest answer and hands it over:
          `noData` is true in exactly this case, and the overview screen has
          always respected it by drawing an em dash and no bar. This grid
          ignored the flag it was given.

          ⚠️ SO `noData` DECIDES, AND THERE IS NO FALLBACK NUMBER ANYWHERE IN
          THIS BLOCK. A skill with nothing behind it gets an em dash, no bar,
          and its caption — which says why.
        */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {sections.motor.map((s) => {
            const meta = motorMeta[s.skill] ?? { subtitle: "" };
            const tint = skillTint(s.skill);

            return (
              <div
                key={s.skill}
                className="flex flex-col justify-between gap-4 rounded-2xl border border-line bg-surface p-5 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                      style={{
                        background: `color-mix(in srgb, ${tint} 16%, var(--color-surface))`,
                        color: `color-mix(in srgb, ${tint} 68%, var(--color-ink))`,
                      }}
                    >
                      <TrendUpIcon size={17} />
                    </span>
                    <div>
                      <h4 className="font-dash text-dash-sm font-extrabold text-ink">
                        {s.label}
                      </h4>
                      {meta.subtitle !== "" && (
                        <p className="font-dash text-dash-xs text-muted">
                          {meta.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* 🔴 AN EM DASH, NEVER AN INVENTED FIGURE. */}
                  <span className="tabular font-dash text-dash-lg font-black text-ink">
                    {s.noData ? "—" : `${String(s.percent)}%`}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {/* ⚠️ NO TRACK AT ALL WHEN THERE IS NOTHING TO FILL IT. An
                      empty bar still reads as "measured, and low". */}
                  {!s.noData && (
                    <div
                      aria-hidden="true"
                      className="h-2 w-full overflow-hidden rounded-full"
                      style={{ background: "var(--color-pill-neutral-bg)" }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-200 ease-dash"
                        style={{
                          width: `${String(s.percent)}%`,
                          background: tint,
                        }}
                      />
                    </div>
                  )}
                  {/* 🔴 THE CAPTION IS NOT OPTIONAL — `skillSections` builds it
                      as "Exercised in 8 of 10 sessions" precisely so the
                      percentage is never read without what it is a percentage
                      of. It is also what explains an em dash. */}
                  <p className="text-[11px] font-semibold text-muted">
                    {s.caption}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Bottom Banner: Parent tip ── */}
      {/*
        🔴 IT NAMED A CHILD CALLED MOLLY, TO EVERY PARENT IN THE PRODUCT.

        The sentence read *"Encourage **Molly** to keep exploring"* — hardcoded,
        under a report about somebody else's four-year-old, on the one screen
        this tier prints and takes to a school. It is the same defect the child
        switcher's note records ("it used to fall back to three invented
        children") surviving in a place nobody re-read.

        ⚠️ THE NAME NOW COMES FROM THE ROSTER, AND THE SENTENCE DROPS IT RATHER
        THAN GREETING A BLANK when the read has not landed. The advice itself is
        addressed to the PARENT and claims nothing about the child — which is why
        it may stay at all, where the fixed sentences of praise this file deleted
        ("Improving steadily") could not.
      */}
      <div className="rounded-2xl border border-rose-100 bg-[#FFF8F6] p-5 shadow-xs flex flex-col gap-1.5">
        <div className="flex items-center gap-2 font-dash text-dash-sm font-extrabold text-rose-900">
          <SparkleIcon size={16} />
          <span>Parent tip</span>
        </div>
        <p className="font-dash text-dash-xs text-rose-800/90 leading-relaxed font-semibold">
          Small steps every day build big skills.{" "}
          {firstName === ""
            ? "Celebrate every effort, however small."
            : `Encourage ${firstName} to keep exploring, and celebrate every effort.`}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── C1 ── */

export interface ChildSwitcherProps extends Base {
  activeChildId: string | null;
  onSwitch: (playerId: string) => void;
  /**
   * 🔴 THE REDUCER IS FED FROM THE REAL CALL, NOT FROM A SECOND SOURCE.
   *
   * `parentFlow` holds `childIds` and refuses to switch to an id outside it —
   * the one transition that could put another parent's child in a request path.
   * That guarantee is only worth anything if the list it checks against is the
   * one the **server** returned, so this component owns the fetch and hands the
   * ids up, rather than the container fetching separately and the two drifting.
   */
  onChildrenLoaded?: (childIds: string[]) => void;
}

/** P8's *"multiple children"*, and the one control at the top of C1. */
export function ChildSwitcher({
  activeChildId,
  lang = "en",
  auth,
  onSwitch,
  onChildrenLoaded,
}: ChildSwitcherProps) {
  const t = PARENT_COPY[lang];
  const resource = useResource<ChildSummary[]>(
    (signal) => getChildren({ ...auth, signal }),
    [],
    {
      isEmpty: (rows) => rows.length === 0,
    },
  );

  const loadedRef = useRef(onChildrenLoaded);
  loadedRef.current = onChildrenLoaded;
  const ids = resource.data?.map((c) => c.playerId).join(",") ?? null;

  useEffect(() => {
    // Keyed on the joined ids rather than on the array, for the reason
    // `useResource` serialises its deps: an array literal is a new object every
    // render, and depending on it would call back on every frame.
    if (ids === null) return;
    loadedRef.current?.(ids === "" ? [] : ids.split(","));
  }, [ids]);

  return (
    <Surface
      resource={resource}
      lang={lang}
      skeletonRows={1}
      emptyMessage={t.noChildren}
    >
      {(children) =>
        /*
         * 🔴 THIS COMPONENT FETCHED THE LIST, HANDED THE IDS UP AND THEN DREW
         * NOTHING — `{() => null}`, with `activeChildId` and `onSwitch`
         * destructured into `_`-prefixed names to keep the linter quiet.
         *
         * `AppRouter` states the contract it was relying on in a comment
         * directly above the call site: *"`ChildSwitcher` draws itself only when
         * there is more than one child, so a one-child account costs nothing for
         * it"* — and `parentFlow` carries a whole `SWITCH_CHILD` transition,
         * guarded against ids outside the server's own list. Reducer, route,
         * props and copy all existed; the control did not. So a parent with two
         * children reached whichever one the reducer picked first and had no way
         * to see the other — P8's *"multiple children"* silently unimplemented.
         *
         * ⚠️ ONE CHILD DRAWS NOTHING, which is the router's stated expectation
         * and not an optimisation. A switcher offering a single option is a
         * control that cannot do anything, above every section of the tier.
         */
        children.length < 2 ? null : (
          /*
           * 🔴 ONE SCROLLING ROW ON A PHONE, WRAPPING ONLY WHERE THERE IS ROOM TO
           * WRAP INTO. `flex-wrap` at every width was measured costing ~100px at
           * the TOP OF EVERY SCREEN IN THE TIER: each pill is a name plus a class
           * ("Aaradhya Venkataraman · Sunflower — Upper KG"), wider than a 360px
           * phone on its own, so two children became two full-width rows under a
           * heading. That is a seventh of a 740px phone spent, above the fold, on
           * a control most parents touch once a session — and it pushed the screen
           * a parent actually came for below the fold on all six routes.
           *
           * A horizontal scroller costs one row whatever the number of children or
           * the length of their class names. `sm:` restores the wrapping row,
           * where the width to wrap into exists.
           *
           * ⚠️ `.scroll-x` RATHER THAN A BARE `overflow-x-auto` — it carries the
           * `overscroll-behavior-x: contain` that stops a swipe along this row
           * becoming a browser back-navigation, which on a phone would throw a
           * parent out of the tier entirely.
           *
           * ⚠️ `-mx-1 px-1` PAIR, AND THE PADDING IS THE POINT. A pill's focus
           * ring is drawn OUTSIDE its border box; with the scroller flush to the
           * content edge the ring on the first and last pill was clipped by the
           * scroll container. The negative margin puts the row back where it was.
           */
          <nav
            aria-label={t.switchChild}
            className="scroll-x -mx-1 mb-4 flex items-center gap-2 px-1 pb-1 sm:mx-0 sm:mb-5 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0 [&>*]:shrink-0"
          >
            {/* ⚠️ HIDDEN ON A PHONE, AND ONLY VISUALLY. The nav's `aria-label`
                already carries these words to a screen reader, so on a phone they
                are a second row of chrome telling a reader what the row beneath
                them self-evidently is. */}
            <span className="hidden font-dash text-dash-xs font-bold text-muted sm:inline">
              {t.switchChild}
            </span>
            {children.map((child) => {
              const active = child.playerId === activeChildId;
              return (
                <button
                  key={child.playerId}
                  type="button"
                  /*
                   * ⚠️ `aria-current`, NOT `aria-pressed`. These are navigation
                   * between children rather than a set of toggles — exactly one
                   * is always chosen, and a screen reader announcing "not
                   * pressed" on the other two would describe a state a parent
                   * cannot put this control into.
                   */
                  aria-current={active ? "true" : undefined}
                  onClick={() => {
                    /* ⚠️ NO-OP ON THE ACTIVE CHILD. Re-dispatching would refetch
                       every panel on the page to arrive at what is already on
                       screen. */
                    if (!active) onSwitch(child.playerId);
                  }}
                  /* ⚠️ `min-h-[44px]` IS 44px — THE PLATFORM TOUCH MINIMUM, AND THIS
                     control was 37px. `py-2` sizes a pill correctly against a
                     mouse and undersizes it against a thumb; the extra 7px costs
                     a phone nothing now the row no longer wraps.

                     ⚠️ `whitespace-nowrap` REPLACES THE OLD `truncate` ON THE
                     NAME. Inside a scroller there is nothing to truncate FOR —
                     the row scrolls — and truncating gave a parent two pills
                     reading "Aaradhya Venk…" and "Bhargav Krish…", which is the
                     one thing this control exists to tell them apart by. */
                  /* 🔴 THE NAME OVER THE CLASS ON A PHONE, SIDE BY SIDE FROM `sm`
                     UP — AND THIS IS WHAT MAKES THE SCROLLING ROW HONEST. Inline,
                     one pill is its name PLUS its class on a single line: 325px
                     of a 360px phone. The second child then sat completely
                     off-screen with nothing protruding to say so, which is a
                     switcher that hides exactly what it exists to switch to.
                     Stacked, a pill is as wide as its widest LINE (~165px), so
                     the next child always breaks the right edge and a parent can
                     see there is someone to scroll to. */
                  className={`flex min-h-[44px] flex-col items-start justify-center whitespace-nowrap rounded-pill border px-3.5 py-1.5 font-dash text-dash-xs font-bold leading-tight transition-colors duration-150 ease-dash sm:flex-row sm:items-center sm:gap-2 sm:py-2 ${
                    active
                      ? "border-primary bg-primary-tint text-primary"
                      : "cursor-pointer border-line bg-surface text-ink hover:bg-canvas"
                  }`}
                >
                  <span>{child.name}</span>
                  {/* ⚠️ THE CLASS IS THE DISAMBIGUATOR, and it is why it is drawn
                      at all: siblings share a surname and a parent picks between
                      them by which room they are in. Which is also why it stays
                      on a phone rather than being hidden to save the width — the
                      one case this control is for is the case that needs it. */}
                  {child.className && (
                    <span
                      className={`text-[10px] sm:text-dash-xs ${
                        active ? "font-medium text-primary" : "font-medium text-muted"
                      }`}
                    >
                      {child.className}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        )
      }
    </Surface>
  );
}

export interface ParentDashboardScreenProps extends Base {
  playerId: string;
  onOpenDetail: () => void;
  onOpenConsent: () => void;
  /**
   * ✅ THE ANNOUNCEMENTS STRIP'S "SEE ALL", AND IT IS A ROUTE RATHER THAN A
   * REDUCER STATE — the same rule `onOpenConsent` follows. Opening the feed by
   * reducer alone would leave the URL saying `parent` while the announcements
   * screen is on the display, so the sidebar would highlight the wrong item and
   * a reload would land elsewhere.
   *
   * ⚠️ OPTIONAL, so a caller that has no route for it simply gets no link rather
   * than a button that does nothing.
   */
  onOpenAnnouncements?: () => void;
  /**
   * 🔴 THE TEACHER CONVERSATION, AND IT IS A DIFFERENT PLACE FROM THE NOTICE
   * BOARD. This screen used to be given `onOpenAnnouncements` for its messages
   * panel, so "View All" and "Contact Teacher" both opened Notices.
   */
  onOpenMessages?: () => void;
  /**
   * C6, from the week strip on the overview.
   *
   * ⚠️ OPTIONAL, LIKE ITS SIBLINGS, so a caller with no route for it gets a
   * panel with no link rather than a button that does nothing.
   */
  onOpenAttendance?: () => void;
  /** C3, from the rewards panel on the overview. */
  onOpenAchievements?: () => void;
  childrenList?: ChildSummary[];
}

/**
 * C1 — *"a 30-second read on a phone"*, which is the design target and a
 * constraint on what is computed as much as on what is drawn.
 *
 * ⚠️ NO "OVERALL %" TILE, THOUGH THE WIREFRAME ASKS FOR ONE. It is OD-39's
 * undefined denominator a level up, and worse: it would have to combine a
 * content LEVEL with a motor RATIO, which are not the same kind of quantity.
 * Any formula would be invented here and printed on a parent's phone as a
 * single headline number about their child. The tiles are counts instead.
 * 📋 Reported.
 */
export function ParentDashboardScreen({
  playerId,
  lang = "en",
  auth,
  onOpenDetail,
  onOpenConsent: _onOpenConsent,
  onOpenAnnouncements: _onOpenAnnouncements,
  onOpenMessages,
  onOpenAttendance,
  onOpenAchievements,
  childrenList,
}: ParentDashboardScreenProps) {
  const resource = useResource<ParentDashboard>(
    (signal) => getDashboard(playerId, { ...auth, signal }),
    [playerId],
  );

  const childrenResource = useResource<ChildSummary[]>(
    (signal) => getChildren({ ...auth, signal }),
    [],
  );

  /*
   * ✅ THE READS C1 WAS DRAWING WITHOUT. The overview rendered invented counts,
   * invented percentages, three games nobody played and two notes no teacher
   * wrote; these are the endpoints that already answered every one of those
   * questions and were simply not being called from this screen.
   *
   * ⚠️ EACH IS ITS OWN RESOURCE, NOT ONE COMBINED READ. `ParentOverviewScreen`
   * degrades per panel — a failed activity read leaves the skills and the
   * messages intact — and a single `Promise.all` would make any one failure
   * blank the whole dashboard.
   */
  const activityResource = useResource<ParentActivity>(
    (signal) => getActivity(playerId, OVERVIEW_WINDOW_DAYS, { ...auth, signal }),
    [playerId],
  );

  const threadResource = useResource<MessageThread>(
    (signal) => getThread(playerId, { ...auth, signal }),
    [playerId],
  );

  /* The panel that used to assert "excellent progress in Mathematics" about
     every child. `Insight` carries the summary, the strengths and the focus
     areas — and `generatedBy`, so the screen can say who wrote it. */
  const insightResource = useResource<Insight>(
    (signal) => getChildInsight(playerId, { ...auth, signal }),
    [playerId],
  );

  /*
   * ✅ THE WEEK'S REGISTER, ON THE SCREEN A PARENT OPENS FIRST.
   *
   * 🔴 C1 IS *"was my child alright this week"* AND IT COULD NOT ANSWER THE HALF
   * OF THAT QUESTION THE SCHOOL OWNS. Attendance lived one nav item away and
   * nowhere on the overview, so a parent whose child had been marked absent on
   * Tuesday learnt it only by going looking. Seven squares is the smallest
   * honest version of that answer.
   *
   * ⚠️ SEVEN DAYS, NOT THE CALENDAR'S 366. C1 is specified as *"a 30-second read
   * on a phone"* — the same argument `OVERVIEW_WINDOW_DAYS` records for the
   * activity read — and the server clamps and states the window it used, so the
   * strip labels itself from the answer.
   */
  const attendanceResource = useResource<ParentAttendance>(
    (signal) => getAttendance(playerId, { ...auth, signal }, OVERVIEW_WINDOW_DAYS),
    [playerId],
  );

  /* The stickers, the coins and the streak — C3's facts, summarised. */
  const progressResource = useResource<ProgressSnapshot>(
    (signal) => getChildProgress(playerId, { ...auth, signal }),
    [playerId],
  );

  return (
    <>
      <ParentOverviewScreen
        lang={lang}
        activeChildId={playerId}
        /*
         * ⚠️ NO STAND-IN CHILD. These passed "Aarav Sharma" and "Class 1 - Blue"
         * whenever the read had not landed, so a parent's first paint named a
         * child who is not theirs. Undefined is what is true until the server
         * answers, and the screen renders a blank rather than a stranger.
         */
        childName={resource.data?.childName}
        dash={resource.data}
        activity={activityResource.data}
        messages={threadResource.data?.messages ?? null}
        insight={insightResource.data}
        childrenList={childrenList || childrenResource.data || undefined}
        onOpenDetail={onOpenDetail}
        onOpenActivity={onOpenDetail}
        /* ✅ THE MESSAGES PANEL GETS THE MESSAGES ROUTE. This read
           `onOpenMessages={onOpenAnnouncements}` — the same navigator as the
           notice board — so both buttons in "Messages from Teacher" opened
           Notices, and "Contact Teacher" led to a page with no teacher on it. */
        onOpenMessages={onOpenMessages}
        attendance={attendanceResource.data}
        progress={progressResource.data}
        onOpenAttendance={onOpenAttendance}
        onOpenAchievements={onOpenAchievements}
        /* The header's notification bell reads `/notices/mine` and
         `/broadcasts/mine` with it; both scope themselves from the token. */
      />

      {/* 🔴 CHANGE 19 §8 — *"collect and track platform feedback directly from
          the Parent Dashboard."* It sits BELOW the overview rather than inside
          it: `ParentOverviewScreen` owns the child's week, and a survey is not
          part of it. It was dropped when this screen was rewritten to delegate,
          which put `FeedbackPrompt` back in the state §8 was raised about —
          written, tested, and reachable by nobody. */}
      <FeedbackPrompt lang={lang} />
    </>
  );
}

/**
 * The way in to H2's survey, on C1.
 *
 * 🔴 LAST ON THE SCREEN AND DELIBERATELY SMALL. C1 is specified as *"a 30-second
 * read on a phone"* about a child; a survey above that is the product asking for
 * something before it has given anything. Below the week's progress, it is asked
 * of somebody who has just been shown why they opened the app.
 *
 * ⚠️ IT IS NOT A NAG AND HAS NO BADGE, NO COUNTER AND NO REPEAT PROMPT. A parent
 * who does not want to answer scrolls past one line; the alternative — a modal
 * on open, or a dot that persists — trains this tier to dismiss things, on the
 * one screen that also carries a school's announcements.
 *
 * ⚠️ THE MODAL SENDS NO TOKEN, WHICH IS `submitFeedback`'s RULE AND NOT AN
 * OVERSIGHT. The row is anonymous: no parent, no child, no school. That is what
 * makes it worth reading, and it is why this card promises anonymity in the copy
 * rather than leaving a parent to assume the opposite.
 */
function FeedbackPrompt({ lang }: { lang: Lang }) {
  const t = PARENT_COPY[lang];
  const [open, setOpen] = useState(false);
  return (
    <>
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">
              {t.feedbackTitle}
            </h2>
            <p className="mt-1 max-w-[60ch] font-dash text-dash-sm text-muted">
              {t.feedbackHint}
            </p>
          </div>
          <DashButton
            variant="secondary"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t.feedbackOpen}
          </DashButton>
        </div>
      </Panel>

      <FeedbackModal
        open={open}
        lang={lang}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}

/* ──────────────────────────────────────────────────────────── announcements ── */

/**
 * One notice, drawn the same way in the strip and in the feed.
 *
 * ⚠️ THE BODY IS `whitespace-pre-line`, WHICH IS THE ONE THING THE CARD OWES THE
 * WRITER. An admin typed this into a textarea and pressed return between
 * paragraphs; collapsing that would run "Sports day is Friday" into "Bring a
 * water bottle" as one sentence. The WhatsApp message flattens it because a
 * template parameter must be one line — this screen is where the notice is
 * shown as it was written.
 */
interface EnrichedNotice extends ParentAnnouncement {
  category?: "Important" | "School Notice" | "Class Notice" | "Event Notice";
  details?: string;
  dateStr?: string;
  timeStr?: string;
  pinned?: boolean;
  attachments?: number;
}

/*
 * ✅ `SPECIMEN_NOTICES` IS GONE, AND IT WAS NOT A PLACEHOLDER.
 *
 * Four invented notices — a Parent-Teacher Meeting on a fixed date, a summer
 * break, a "Kalkis Learning Week", all attributed to a "Kalkis Academy" that is
 * not even this product's name — rendered whenever a parent had NO real
 * announcements. That is the state most families are in most of the time, so
 * the fallback was the common path: a parent could have shown up to a meeting
 * their school never called.
 *
 * The read already declares `isEmpty` below, so the surface has a real empty
 * state; this fallback was overriding it.
 */

function AnnouncementCard({
  announcement,
  lang,
  live = false,
}: {
  announcement: EnrichedNotice;
  lang: Lang;
  live?: boolean;
}) {
  const t = PARENT_COPY[lang];

  const category = announcement.category || (announcement.forClass ? "Class Notice" : "School Notice");
  const isImportant = category === "Important" || announcement.pinned;

  /*
   * 🔴 THE CATEGORY PICKS THE ICON, AND THERE IS NO EMOJI FIELD ANY MORE.
   * This was four emoji chosen by category, with a fifth branch below testing
   * `icon === "💜"` — a colour decision keyed off a heart glyph that nothing
   * ever set, so the purple branch was unreachable. `EnrichedNotice.icon` was
   * assigned client-side a few hundred lines away, never sent by the server,
   * so the whole field was a local detour to reach a constant.
   */
  const CategoryIcon =
    category === "Important"
      ? AlertIcon
      : category === "Event Notice"
        ? CalendarIcon
        : category === "Class Notice"
          ? ChildrenIcon
          : VoiceIcon;
  
  const createdDate = new Date(announcement.createdAt);
  const formattedDate = announcement.dateStr || createdDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const formattedTime = announcement.timeStr || createdDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  /*
   * The category's colour, as tokens and as one lookup.
   *
   * 🔴 THIS WAS AN `if/else if` LADDER OVER TWELVE TAILWIND CLASSES, AND ONE
   * OF ITS BRANCHES WAS UNREACHABLE. The "Event Notice" arm split again on
   * `icon === "💜"` — a test against a heart emoji that nothing in this file,
   * or anywhere else, ever assigned — so the purple styling could not be
   * reached and every event notice took the amber `else`. Removing the dead
   * branch is what collapses this to a table.
   *
   * ⚠️ FOUR PILL FAMILIES, NOT FOUR HAND-PICKED PAIRS. `pill-*-bg/fg/line`
   * exist so a status chip is the same chip everywhere and its text passes
   * contrast on its own fill — which `text-sky-700 on bg-sky-100` was not
   * checked for.
   *
   * ⚠️ `badgeStyle` AND `tagStyle` WERE ALWAYS ASSIGNED THE SAME VALUE, in all
   * five arms. They are one constant now.
   */
  const CATEGORY_PILL: Record<string, string> = {
    Important:
      "bg-pill-danger-bg text-pill-danger-fg border-pill-danger-line",
    "Class Notice":
      "bg-pill-success-bg text-pill-success-fg border-pill-success-line",
    "Event Notice":
      "bg-pill-warning-bg text-pill-warning-fg border-pill-warning-line",
    "School Notice":
      "bg-pill-neutral-bg text-pill-neutral-fg border-pill-neutral-line",
  };
  const badgeStyle =
    CATEGORY_PILL[category] ?? CATEGORY_PILL["School Notice"] ?? "";
  const tagStyle = badgeStyle;

  return (
    <article
      className={`rounded-2xl border transition-all duration-200 p-5 shadow-2xs hover:shadow-card flex flex-col md:flex-row md:items-center justify-between gap-4 ${
        isImportant
          ? "bg-rose-50/40 border-rose-100 hover:border-rose-200"
          : "bg-surface border-line hover:border-primary/30"
      }`}
    >
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div
          className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 border text-xl ${badgeStyle}`}
        >
          <CategoryIcon size={22} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">
              {announcement.title}
            </h3>
            <span
              className={`font-dash text-[11px] font-extrabold px-2.5 py-0.5 rounded-full border ${tagStyle}`}
            >
              {category}
            </span>
            {live && (
              <StatusPill tone="success" icon={<InfoIcon size={14} />}>
                {t.announcementNew}
              </StatusPill>
            )}
          </div>

          <p className="mt-1.5 text-dash-xs font-semibold text-muted leading-relaxed max-w-3xl">
            {announcement.body}
          </p>

          {announcement.details && (
            <p className="mt-2 text-dash-xs font-bold text-ink/80">
              {announcement.details}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center md:items-end justify-between md:flex-col gap-3 shrink-0 text-right border-t md:border-t-0 border-line/60 pt-3 md:pt-0">
        <div className="flex flex-col items-start md:items-end">
          <div className="flex items-center gap-1.5 text-dash-xs font-bold text-ink">
            <CalendarIcon size={14} className="text-muted" />
            <span>{formattedDate}</span>
          </div>
          <div className="text-[11px] font-semibold text-muted mt-0.5">
            {formattedTime}
          </div>
        </div>

        <div className="flex items-center gap-3 text-muted">
          {announcement.attachments ? (
            <span
              className="flex items-center gap-1 text-dash-xs font-bold text-muted hover:text-ink transition-colors cursor-pointer"
              title={`${announcement.attachments} attachments`}
            >
              <DownloadIcon size={13} />
              <span>{announcement.attachments}</span>
            </span>
          ) : null}
          {announcement.pinned ? (
            <span className="text-primary" title={t.announcementNew}>
              <AlertIcon size={14} />
            </span>
          ) : null}
          <button
            type="button"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center p-1 rounded-lg hover:bg-canvas text-muted hover:text-ink transition-colors cursor-pointer"
            title="More options"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="1" />
              <circle cx="12" cy="5" r="1" />
              <circle cx="12" cy="19" r="1" />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
}

/**
 * The three newest notices, on C1.
 */
export function AnnouncementsStrip({
  lang,
  auth,
  onOpenAll,
}: {
  lang: Lang;
  auth?: ParentAuth;
  onOpenAll?: () => void;
}) {
  const t = PARENT_COPY[lang];
  const resource = useResource<ParentAnnouncement[]>(
    (signal) => getParentAnnouncements({ ...auth, signal }),
    ["parent-announcements-strip"],
    { isEmpty: (rows) => rows.length === 0 },
  );
  const live = useParentEvents(auth?.token);

  const rows = mergeAnnouncements(resource.data ?? [], live, {
    schoolName: "",
  } as Omit<
    ParentAnnouncement,
    "id" | "title" | "body" | "authorLabel" | "createdAt"
  >);

  if (rows.length === 0) return null;

  return (
    <>
      <SectionHead count={rows.length}>{t.announcementsLatest}</SectionHead>
      <div className="flex flex-col gap-3">
        {rows.slice(0, 3).map((a) => (
          <AnnouncementCard
            key={a.id}
            announcement={a}
            lang={lang}
            live={live.some((e) => e.id === a.id)}
          />
        ))}
        {onOpenAll && rows.length > 3 ? (
          <p>
            <DashButton variant="ghost" onClick={onOpenAll}>
              {t.announcementsSeeAll}
            </DashButton>
          </p>
        ) : null}
      </div>
    </>
  );
}

/**
 * The announcements screen — the school's notice board, in full.
 */
export function AnnouncementsScreen({ lang = "en", auth }: Base) {
  const resource = useResource<ParentAnnouncement[]>(
    (signal) => getParentAnnouncements({ ...auth, signal }),
    ["parent-announcements"],
    { isEmpty: (rows) => rows.length === 0 },
  );
  const live = useParentEvents(auth?.token);

  const merged = mergeAnnouncements(resource.data ?? [], live, {
    schoolName: "",
  } as Omit<
    ParentAnnouncement,
    "id" | "title" | "body" | "authorLabel" | "createdAt"
  >);

  const allNotices: EnrichedNotice[] = useMemo(() => {
    return merged.map((a) => ({
      ...a,
      category: a.forClass ? "Class Notice" : "School Notice",
    }));
  }, [merged]);

  const [activeTab, setActiveTab] = useState<string>("All Notices");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(5);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const filteredNotices = useMemo(() => {
    return allNotices.filter((notice) => {
      if (activeTab === "School Notices" && notice.category !== "School Notice") return false;
      if (activeTab === "Class Notices" && notice.category !== "Class Notice") return false;
      /*
       * ✅ NO "Event Notices" BRANCH — THE TAB IS GONE AND SO IS ITS CATEGORY.
       * A real notice is categorised from `forClass`, which yields only
       * "Class Notice" or "School Notice"; "Event Notice" existed solely on the
       * invented `SPECIMEN_NOTICES` rows. The tab could therefore never match
       * anything once those went, which is a filter that always returns nothing
       * and reads as a fault.
       */

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = notice.title.toLowerCase().includes(q);
        const matchBody = notice.body.toLowerCase().includes(q);
        const matchDetails = notice.details?.toLowerCase().includes(q) ?? false;
        if (!matchTitle && !matchBody && !matchDetails) return false;
      }
      return true;
    });
  }, [allNotices, activeTab, searchQuery]);

  const totalItems = filteredNotices.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const safePage = Math.min(currentPage, totalPages);

  const paginatedNotices = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredNotices.slice(start, start + pageSize);
  }, [filteredNotices, safePage, pageSize]);

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6">
      {/*
        ✅ THE PAGE HEADER IS GONE, ON REQUEST.
        It restated what the sidebar already says — a parent who has just tapped
        "Notices" in a highlighted nav item does not need the word again above
        the list, plus a sentence explaining that notices are updates.

        ⚠️ AND IT WAS A SECOND `<h1>`. `DashboardShell` records the same defect:
        one `<h1>` per document is what the outline algorithm is built on, and a
        screen-reader user heard the page title twice.
      */}

      {/* Filter Tabs & Search Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          {["All Notices", "School Notices", "Class Notices"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              className={`min-h-[44px] px-4 py-2 rounded-xl text-dash-xs font-extrabold whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === tab
                  ? "bg-purple-600 text-white shadow-2xs"
                  : "bg-surface border border-line text-ink hover:bg-canvas"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/*
          ✅ THE SELECT NEXT TO THIS IS GONE, ON REQUEST — AND IT WAS A DUPLICATE
          RATHER THAN A SECOND OPINION. It read and wrote the SAME `activeTab`
          state as the tab row on the left, with the same three values, sitting
          on the same line: choosing "Class Notices" in either one moved the
          other. Two controls for one value is a screen that looks like it
          filters twice, and it is the shape a parent reads as "I must set both".

          ⚠️ THE TABS ARE THE ONE THAT STAYS, not the dropdown. They show the
          current filter without being opened, which is what a *"30-second read
          on a phone"* needs, and they were already the wider control.
        */}
        <div className="flex items-center shrink-0">
          <div className="relative flex-1 md:w-64">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
              <SearchIcon size={15} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search notices..."
              className="h-10 min-h-[44px] pointer-fine:min-h-0 w-full rounded-xl border border-line bg-surface pl-9 pr-3.5 text-dash-xs font-semibold text-ink placeholder:text-muted outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Notice List */}
      <div className="flex flex-col gap-4">
        {paginatedNotices.length > 0 ? (
          paginatedNotices.map((n) => (
            <AnnouncementCard
              key={n.id}
              announcement={n}
              lang={lang}
              live={live.some((e) => e.id === n.id)}
            />
          ))
        ) : (
          <div className="rounded-2xl border border-line bg-surface p-12 text-center text-muted">
            <p className="text-dash-md font-bold">No notices found</p>
            <p className="text-dash-xs mt-1">Try adjusting your filters or search query.</p>
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-line/60">
        <div className="text-dash-xs font-bold text-muted">
          Showing {totalItems > 0 ? (safePage - 1) * pageSize + 1 : 0} to{" "}
          {Math.min(safePage * pageSize, totalItems)} of {totalItems} notices
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            disabled={safePage === 1}
            onClick={() => setCurrentPage(safePage - 1)}
            className="h-[44px] w-[44px] pointer-fine:h-8 pointer-fine:w-8 rounded-lg border border-line bg-surface flex items-center justify-center font-bold text-ink disabled:opacity-40 hover:bg-canvas transition-colors cursor-pointer"
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setCurrentPage(p)}
              className={`h-[44px] w-[44px] pointer-fine:h-8 pointer-fine:w-8 rounded-lg font-bold text-dash-xs transition-colors cursor-pointer ${
                p === safePage
                  ? "bg-purple-600 text-white shadow-2xs"
                  : "border border-line bg-surface text-ink hover:bg-canvas"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={safePage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(safePage + 1)}
            className="h-[44px] w-[44px] pointer-fine:h-8 pointer-fine:w-8 rounded-lg border border-line bg-surface flex items-center justify-center font-bold text-ink disabled:opacity-40 hover:bg-canvas transition-colors cursor-pointer"
          >
            ›
          </button>
        </div>

        <div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="h-[44px] pointer-fine:h-9 rounded-xl border border-line bg-surface px-3 text-dash-xs font-bold text-ink cursor-pointer outline-none focus:border-primary transition-colors"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={15}>15 per page</option>
          </select>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── C2 ── */

export interface ProgressDetailScreenProps extends Base {
  playerId: string;
}

/** The window C2 reads. Matches C6's calendar so the two screens agree on a day. */
const DETAIL_WINDOW_DAYS = 28;

/**
 * C2 — per-skill bars, the numbers, the day-by-day feed, and the printed report.
 *
 * 🔴 "EXPORT PDF" IS STILL `window.print()`, AND NOW THERE IS SOMETHING TO
 * PRINT. OD-59 refused a server-side renderer for D7 and all three of its
 * reasons hold: a rendering dependency on a route that emits children's names,
 * no specified layout, and puppeteer would put headless Chromium on a Lightsail
 * node sized on cost grounds. What was missing was the other half — the shell is
 * `h-dvh overflow-hidden` with the page in a scroller, so the browser was handed
 * ONE VIEWPORT and produced a single sheet with a sidebar down the left and the
 * child's progress clipped off the bottom. `ReportSheet` and `print.css` are that
 * half: a document that flows onto as many pages as it needs, with the chrome
 * gone and the entrance animations frozen at their finished state.
 *
 * ✅ AND THE THREE-COLUMN ACTIVITY TABLE IS A DAY-BY-DAY FEED NOW. It printed one
 * row per EVENT — a date, a game slug and "NUMERACY L2" — so one afternoon filled
 * the screen with forty near-identical lines and a parent scrolled past their
 * child's week without seeing a day in it. The question is *"what did they do on
 * Tuesday"*, and that is a day.
 *
 * ⚠️ THE TRADE, RECORDED AND UNCHANGED: there is no server-generated PDF, so
 * §9.2's WhatsApp digest still cannot attach one. It has to be a message with
 * numbers in it and a link back here. 📋 Reported.
 */
export function ProgressDetailScreen({
  playerId,
  lang = "en",
  auth,
}: ProgressDetailScreenProps) {
  const t = PARENT_COPY[lang];
  const dash = useResource<ParentDashboard>(
    (signal) => getDashboard(playerId, { ...auth, signal }),
    [playerId],
  );
  /*
   * 🔴 ONE READ FOR THE CHARTS, THE FEED AND THE REPORT — and it replaced
   * `GET /players/:playerId/events`, which answered a different question. That
   * route returns the most recent fifty rows, flat and capped: a chart drawn from
   * it would have edges at "wherever fifty rows ran out" rather than at dates,
   * and a feed built from it could not show a day with no events (a sitting that
   * recorded nothing is still something that happened).
   */
  const activity = useResource<ParentActivity>(
    (signal) => getActivity(playerId, DETAIL_WINDOW_DAYS, { ...auth, signal }),
    [playerId],
  );
  /* The sticker ids and the coin total, for the report's rewards block. */
  const progress = useResource<ProgressSnapshot>(
    (signal) => getChildProgress(playerId, { ...auth, signal }),
    [playerId],
  );
  const insight = useResource<Insight>(
    (signal) => getChildInsight(playerId, { ...auth, signal }),
    [playerId],
  );

  /*
   * ⚠️ THE CLOCK IS READ ONCE PER MOUNT, NOT PER RENDER. `istToday` and the
   * report's timestamp both need "now", and a component that called `new Date()`
   * in its body would re-derive them on every keystroke elsewhere on the page —
   * and could print a report dated a millisecond after the one on screen.
   */
  const now = useMemo(() => new Date(), []);

  /** Whether the numbers behind the report are showing. See `SkillSections`. */
  const [showNumbers, setShowNumbers] = useState(false);

  return (
    <section className="font-dash text-dash-sm text-ink">
      <Surface resource={dash} lang={lang}>
        {(d) => (
          <>
            {/*
              🔴 THE SHEET IS IN THE DOM AND HIDDEN, RATHER THAN BUILT ON DEMAND.
              `window.print()` is synchronous — there is no moment between the
              click and the dialogue in which to fetch anything — so everything
              the document needs has to be loaded already. It takes each resource's
              `data` rather than waiting on it: the report prints what arrived and
              says nothing about what did not.
            */}
            <ReportSheet
              lang={lang}
              dash={d}
              activity={activity.data}
              progress={progress.data}
              insight={insight.data}
              now={now}
            />

            {/* ⚠️ EVERYTHING BELOW IS `print:hidden`. The screen and the sheet are
                two documents about the same child, and printing both would put a
                sidebar-less copy of the app underneath the report. */}
            <div className="print:hidden">
              <PageHead
                actions={
                  <span className="flex flex-wrap items-center gap-2">
                    <DashButton
                      variant="secondary"
                      onClick={() => window.print()}
                    >
                      <DownloadIcon size={16} />
                      {t.printReport}
                    </DashButton>
                    {/*
                      ✅ NO "MESSAGE THE TEACHER" HERE, ON REQUEST. This screen's
                      one action is the print. The conversation itself is not
                      gone — it is the Messages nav item (C4), which is where a
                      parent who wants to write to the teacher goes.
                    */}
                  </span>
                }
              />

              <SkillSections
                dash={d}
                lang={lang}
                breakdownOpen={showNumbers}
                onToggleBreakdown={() => {
                  setShowNumbers((v) => !v);
                }}
              />

              {/*
                ✅ THE PANEL THE BREAKDOWN BUTTON PROMISED, RENDERED AT LAST.
                `ProgressNumbers` was written, exported and called by nobody: this
                child's real day counts, minutes, answers and weekday pattern,
                every ratio printed as its own division, sitting in the file
                unreachable. It reads `GET …/activity?days=28` — a genuinely
                windowed route, unlike the cumulative one the cards above use.

                ⚠️ COLLAPSED BY DEFAULT. C2 opens as the printable report; the
                arithmetic behind it is a second question, asked by the parent who
                wants it rather than shown to the one who does not.
              */}
              {showNumbers && (
                <div className="mt-8">
                  <ProgressNumbers resource={activity} lang={lang} />
                </div>
              )}
            </div>
          </>
        )}
      </Surface>
    </section>
  );
}

/**
 * The numbers section — C2's charts, and the arithmetic behind each one.
 *
 * 🔴 EVERY RATIO IS PRINTED AS ITS DIVISION. OD-39, contract D4, and
 * `ParentService`'s own refusal of an "overall %" tile: a percentage a parent
 * cannot check is a measurement claim about their child, and the first question a
 * school asks is *"percent of what?"*. So "38 of 46 answers" is the headline and
 * "83%" is the footnote, on every card here.
 *
 * 🔴 AND THERE IS NO TARGET, NO BENCHMARK AND NO OTHER CHILD ANYWHERE ON IT. The
 * tallest bar is this child's own busiest day; the weekday rows divide by how
 * many of each weekday the window held. Both of those are comparisons a parent
 * can verify. A class average would be an invented denominator with somebody
 * else's child inside it.
 */
export function ProgressNumbers({
  resource,
  lang,
}: {
  resource: Resource<ParentActivity>;
  lang: Lang;
}) {
  const t = PARENT_COPY[lang];

  return (
    <>
      <SectionHead>{t.numbers}</SectionHead>
      <Surface resource={resource} lang={lang} skeletonRows={3}>
        {(a) => {
          const right = accuracy(a.totals);
          const played = consistency(a);
          const minutes = perActiveDay(a.totals.minutes, a.totals);
          const bars = series(a.days, (d) => d.events);
          const shares = skillShares(a.days);
          const best = busiestDay(a.days);
          const locale = "en-IN";

          if (a.totals.events === 0) {
            /* ⚠️ NOT A `DataSurface` EMPTY STATE. The read succeeded and the
               answer is "nothing yet", which is a fact about a child rather than
               a failure — and on this tier those two must never look alike. */
            return (
              <Panel>
                <p className="text-muted">{t.numbersEmpty}</p>
              </Panel>
            );
          }

          return (
            <>
              <p className="-mt-3 mb-4 text-dash-xs text-muted">
                {t.numbersHint.replace("{days}", String(a.windowDays))}
              </p>
              {a.truncated && (
                <div className="mb-4">
                  <Note tone="warning" icon={<InfoIcon size={16} />}>
                    {t.numbersTruncated}
                  </Note>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {right ? (
                  <AccuracyRing
                    ratio={right}
                    label={t.metricAccuracy}
                    caption={t.metricAccuracyOf(right.value, right.of)}
                  />
                ) : (
                  <MetricCard
                    label={t.metricAccuracy}
                    value="—"
                    detail={t.metricAccuracyNone}
                    tint="var(--color-sky)"
                    icon={<CheckIcon size={18} />}
                  />
                )}

                <MetricCard
                  label={t.metricDaysPlayed}
                  /* 🔴 THE FRACTION IS THE VALUE. `9 / 28`, not "32%". */
                  value={
                    played
                      ? `${String(played.value)} / ${String(played.of)}`
                      : "—"
                  }
                  detail={
                    played
                      ? t.metricDaysPlayedOf(played.value, played.of)
                      : undefined
                  }
                  tint="var(--color-leaf)"
                  icon={<TrendUpIcon size={18} />}
                />

                <MetricCard
                  label={t.metricMinutes}
                  value={t.metricMinutesValue(a.totals.minutes)}
                  detail={
                    minutes
                      ? t.metricMinutesPerDay(minutes.percent, minutes.of)
                      : undefined
                  }
                  tint="var(--color-indigo)"
                  icon={<PlayIcon size={18} />}
                />

                <MetricCard
                  label={t.metricLevels}
                  value={a.totals.levelUps}
                  detail={t.metricLevelsHint}
                  tint="var(--color-primary)"
                  icon={<StickerIcon size={18} />}
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Panel>
                  <p className="mb-1 font-dash text-dash-sm font-bold text-ink">
                    {t.chartActivity}
                  </p>
                  <p className="mb-4 text-dash-xs text-muted">
                    {t.chartActivityHint}
                  </p>
                  <DayBars
                    series={bars}
                    maxLabel={t.chartActivityMax(bars.max)}
                    describe={(day, value) =>
                      `${new Date(`${day}T00:00:00.000Z`).toLocaleDateString(
                        locale,
                        {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          timeZone: "UTC",
                        },
                      )} — ${t.activityRounds(value)}`
                    }
                  />
                  {best && (
                    <p className="mt-3 text-dash-xs text-muted">
                      {t.metricBusiest(
                        new Date(
                          `${best.day}T00:00:00.000Z`,
                        ).toLocaleDateString(locale, {
                          day: "numeric",
                          month: "long",
                          timeZone: "UTC",
                        }),
                        best.events,
                      )}
                    </p>
                  )}
                </Panel>

                <Panel>
                  <p className="mb-1 font-dash text-dash-sm font-bold text-ink">
                    {t.chartWeekday}
                  </p>
                  <p className="mb-4 text-dash-xs text-muted">
                    {t.chartWeekdayHint}
                  </p>
                  <WeekdayBars
                    rows={weekdays(a.days)}
                    weekdayLabels={weekdayLabels()}
                    format={t.chartWeekdayOf}
                  />
                </Panel>
              </div>

              {shares.length > 0 && (
                <div className="mt-4">
                  <Panel>
                    <p className="mb-1 font-dash text-dash-sm font-bold text-ink">
                      {t.chartSkills}
                    </p>
                    <p className="mb-4 text-dash-xs text-muted">
                      {t.chartSkillsHint}
                    </p>
                    <SkillBars
                      shares={shares}
                      name={(skill) => skillLabel(skill, lang)}
                      format={t.chartSkillsOf}
                      total={shares.reduce((n, s) => n + s.events, 0)}
                    />
                  </Panel>
                </div>
              )}
            </>
          );
        }}
      </Surface>
    </>
  );
}

/**
 * Sunday first, matching `Date.getUTCDay`.
 *
 * ⚠️ BUILT FROM `Intl`, NOT TYPED OUT. `attendanceCalendar.ts` derives the
 * calendar's own weekday letters the same way — two hand-written lists are two
 * lists that disagree the first time one is corrected.
 */
function weekdayLabels(): string[] {
  const fmt = new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    timeZone: "UTC",
  });
  /* 4 January 1970 was a Sunday. */
  return Array.from({ length: 7 }, (_, i) =>
    fmt.format(new Date(Date.UTC(1970, 0, 4 + i))),
  );
}

/* ─────────────────────────────────────────────────────────────── C3 ── */

export interface AchievementsScreenProps extends Base {
  playerId: string;
}

/**
 * C3 — *"a parent-visible view of the child's rewards"*.
 *
 * It reads `GET /progress/:playerId`, which already exists and which
 * `OwnPlayerGuard` already admits a parent to for their own child. A
 * parent-shaped copy of that route would be a second implementation of a read
 * that works.
 */
export function AchievementsScreen({
  playerId,
  lang = "en",
  auth,
}: AchievementsScreenProps) {
  const t = PARENT_COPY[lang];
  const resource = useResource<ProgressSnapshot>(
    (signal) => getChildProgress(playerId, { ...auth, signal }),
    [playerId],
    { isEmpty: (p) => p.stickers.length === 0 && p.coins === 0 },
  );

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead title={t.achievements} subtitle={t.rewardsHint} />
      <Surface
        resource={resource}
        lang={lang}
        skeletonRows={3}
        emptyMessage={t.noAchievements}
      >
        {(p) => (
          /*
            🔴 WHAT WAS HERE FIRST: a row of counts over `StatusPill`s printing
            the raw sticker ids — `sky-quest-l2`, `letter-lagoon-l1` — at a
            family. `stickerBook` fixed that half for good: the shelf names the
            game and the level and shows the plate the child taps.

            ⚠️ AND THE SECOND FIX OVERSHOT. The tiles were then given the whole
            explanation — a paragraph, a worked sum and a note inside each of the
            three cards — which answered the question but buried the number a
            parent came to see. They are the kit's `RewardTile` now, the same one
            the teacher's Leaderboard draws, so the figure is the loud part on
            both tiers.
          */
          <RewardsPanel
            lang={lang}
            coins={p.coins}
            stickers={p.stickers}
            streakDays={p.streakDays}
          />
        )}
      </Surface>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── C6 ── */

export interface AttendanceScreenProps extends Base {
  playerId: string;
}

/**
 * C6 — *"was my child in school?"*, which is the question a parent asks a school
 * most often and which this product could not answer anywhere.
 *
 * 🔴 IT SHOWS DAYS, NEVER A PERCENTAGE, and the absence of that number is the
 * screen's one real design decision. `Absence` holds absences: a day on which
 * every child came writes no row, so "94% attendance" needs a denominator the
 * database does not have. The two ways to fake one are counting days on which
 * SOMEBODY in the class was absent (which makes a good week look worse) and
 * counting calendar days (which charges a family for Sundays). Both would print
 * an invented number about somebody's child on their phone, and a parent would
 * take it to the school as fact.
 *
 * 🔴 AND IT SAYS WHAT AN EMPTY SCREEN MEANS. A register nobody took writes no
 * rows, which is identical in the data to perfect attendance — so the hint is
 * not decoration, it is the difference between an honest screen and a
 * reassuring one.
 */
export function AttendanceScreen({
  playerId,
  lang = "en",
  auth,
}: AttendanceScreenProps) {
  const resource = useResource<ParentAttendance>(
    (signal) => getAttendance(playerId, { ...auth, signal }, ATTENDANCE_WINDOW_DAYS),
    [playerId],
  );
  /*
   * ✅ THE SECOND READ IS WHAT MAKES A SQUARE WORTH TAPPING.
   *
   * 🔴 THE CALENDAR HAS ALWAYS BEEN A ROW OF CONTROLS THAT DID NOTHING. A parent
   * taps a coloured, rounded, tooltipped box — everyone does — and the only thing
   * the product could say about that day was the colour the square already had.
   * *"What did they actually do on Tuesday?"* is the question the grid itself
   * puts in a parent's head, and the answer has been in the events table all
   * along.
   *
   * ⚠️ IT IS A SEPARATE RESOURCE, AND THE SCREEN WORKS WITHOUT IT. The register
   * is the school's record and the play is the child's; if this read fails the
   * calendar still draws, the squares still open, and the panel says there is no
   * play recorded. The alternative — one combined endpoint — would let a failure
   * in the newer half blank the older one.
   */
  const activity = useResource<ParentActivity>(
    (signal) =>
      getActivity(playerId, ATTENDANCE_ACTIVITY_DAYS, { ...auth, signal }),
    [playerId],
  );

  return (
    <section className="font-dash text-dash-sm text-ink">
      <Surface resource={resource} lang={lang} skeletonRows={3}>
        {(a) => (
          <Attendance a={a} lang={lang} activity={activity.data} auth={auth} />
        )}
      </Surface>
    </section>
  );
}

/**
 * How far back C6 asks, now that the arrows walk through months.
 *
 * 🔴 A YEAR, BECAUSE THE ARROWS HAD NOWHERE TO GO. The screen asked for the
 * server's default 28 days and drew "‹" and "›" beside the grid, so the only
 * honest thing they could ever have done was refuse to move — a parent looking
 * for last term's absences was looking at a control that could not reach them.
 * The server clamps this to 366 and states the window it used; `windowStart`
 * below is read from the ANSWER, so a month past it draws as unknown rather than
 * as "nobody took the register".
 */
const ATTENDANCE_WINDOW_DAYS = 366;

/**
 * ⚠️ SHORTER THAN THE CALENDAR'S WINDOW, AND THE SCREEN SAYS SO RATHER THAN
 * HIDING IT. `GET …/activity` clamps at 120 days — it folds every event and
 * session in the window, which a year of would be a large read on a phone — so
 * the day detail can answer "what did they play" for the last four months and
 * only the register for anything older. `DayDetail` already renders that case;
 * what it must not do is imply the child played nothing.
 */
const ATTENDANCE_ACTIVITY_DAYS = 120;

/**
 * One day's register state, as a pill.
 *
 * 🔴 THE WORDS ARE THE LEGEND'S WORDS. The grid teaches a vocabulary — "the
 * register marked them in", "marked absent", "school closed" — and a table
 * beside it using different nouns for the same four states would be a second
 * vocabulary for a parent to learn on one screen.
 *
 * ⚠️ SHAPE AND TEXT, NOT ONLY COLOUR (§12). The label carries the meaning; the
 * tint is a reinforcement, which is why there is no icon-only variant.
 */
function AttendanceStatusPill({
  state,
  lang,
}: {
  state: AttendanceState;
  lang: Lang;
}) {
  const t = PARENT_COPY[lang];
  const map: Record<AttendanceState, { label: string; colour: string }> = {
    PRESENT: { label: t.attendanceLegendPresent, colour: "var(--color-leaf)" },
    ABSENT: { label: t.attendanceLegendAbsent, colour: "var(--color-coral)" },
    HOLIDAY: { label: t.attendanceLegendHoliday, colour: "var(--color-grape)" },
    NO_REGISTER: {
      label: t.attendanceLegendUnknown,
      colour: "var(--color-muted)",
    },
  };
  const { label, colour } = map[state];
  return (
    <span
      className="inline-block rounded-pill px-2.5 py-0.5 font-dash text-[11px] font-extrabold"
      style={{
        background: `color-mix(in srgb, ${colour} 16%, transparent)`,
        color: colour,
      }}
    >
      {label}
    </span>
  );
}

/**
 * The month picker that sits on the Overview and Calendar cards.
 *
 * 🔴 IT WAS A `<div>` WITH `cursor-pointer` AND NOTHING BEHIND IT. A chip that
 * carries a caret and changes the cursor is a control to everyone who sees it;
 * one that never opens reads as a broken screen rather than as decoration. This
 * is the menu that chip was already promising.
 *
 * ⚠️ THE OPTIONS STOP WHERE THE ANSWER DOES. `monthsInWindow` derives them from
 * `windowStart` — the oldest day the server said it covered — so a month is on
 * this list only if there are squares to draw in it. Offering last March because
 * a month name exists would be offering a grid of days nobody was asked about.
 */
function RangeSelect({
  value,
  label,
  options,
  onPick,
}: {
  /** The open month, as `"2026-7"`. */
  value: string;
  /** What the closed chip reads — the month being drawn. */
  label: string;
  options: readonly { year: number; month: number; label: string }[];
  onPick: (choice: { year: number; month: number }) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  /* Pointer and keyboard both dismiss it, for the same reason the day dialogue
     closes on Escape: a layer only its own button can shut is a trap. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="min-h-[44px] pointer-fine:min-h-0 rounded-xl border border-slate-200 bg-white px-3 py-1 text-dash-xs font-bold text-slate-700 shadow-2xs flex items-center gap-1.5 hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <span>{label}</span>
        <span aria-hidden="true" className="text-slate-400 text-[10px]">
          ▼
        </span>
      </button>

      {open && (
        /* ⚠️ CAPPED AND SCROLLING. A year's window is thirteen months, which is
           taller than the card it hangs off. */
        <ul
          role="listbox"
          className="absolute right-0 top-full z-30 mt-2 max-h-64 w-44 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-2xl"
        >
          {options.map((m) => {
            const key = `${String(m.year)}-${String(m.month)}`;
            return (
              <li key={key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={key === value}
                  onClick={() => {
                    onPick({ year: m.year, month: m.month });
                    setOpen(false);
                  }}
                  className={`w-full px-3.5 py-2 text-left font-dash text-dash-xs font-bold transition-colors hover:bg-slate-50 ${
                    key === value ? "text-primary" : "text-slate-600"
                  }`}
                >
                  {m.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The window as one ring: present, absent, and the days nobody marked.
 *
 * 🔴 THREE ARCS, NOT ONE FILLED TO 96%. A single arc would be a progress bar,
 * and attendance is not progress toward anything — the remainder has to be
 * *identifiable*, because "not present" splits into "absent" and "no register
 * was taken" and those mean opposite things to a parent. `AttendanceState` calls
 * conflating them the one dishonesty this screen exists to prevent.
 *
 * ⚠️ `aria-hidden`, AND NOT AN OVERSIGHT. Every number in it is in the list
 * beside it, as text, with its label — so a screen reader reads the figures
 * rather than an SVG that cannot carry them.
 */
function AttendanceRing({
  tally,
}: {
  tally: { present: number; absent: number; noRecord: number; total: number; presentPct: number };
}) {
  const R = 42;
  const C = 2 * Math.PI * R;
  const seg = (n: number) => (tally.total === 0 ? 0 : (n / tally.total) * C);
  const presentLen = seg(tally.present);
  const absentLen = seg(tally.absent);

  return (
    <div className="relative h-36 w-36 shrink-0" aria-hidden="true">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        {/* Base ring: no register */}
        <circle cx="50" cy="50" r={R} fill="none" stroke="#E2E8F0" strokeWidth="10" />
        {/* Present ring */}
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke="#22C55E"
          strokeWidth="10"
          strokeDasharray={`${String(presentLen)} ${String(C - presentLen)}`}
        />
        {/* Absent ring */}
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke="#F43F5E"
          strokeWidth="10"
          strokeDasharray={`${String(absentLen)} ${String(C - absentLen)}`}
          strokeDashoffset={-presentLen}
        />
      </svg>
      {/* Center Smiley Face Icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-16 w-16 rounded-full bg-[#FFD028] flex items-center justify-center shadow-2xs">
          <svg viewBox="0 0 36 36" className="w-10 h-10 text-amber-900 fill-current">
            <circle cx="12" cy="14" r="2.2" />
            <circle cx="24" cy="14" r="2.2" />
            <path d="M 10 20 Q 18 28 26 20" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}

/**
 * Split out so the calendar can be built with a hook, which a render prop
 * cannot hold.
 */
function Attendance({
  a,
  lang,
  activity,
  auth: _auth,
}: {
  a: ParentAttendance;
  lang: Lang;
  /** Null while the activity read is in flight, or if it failed. */
  activity: ParentActivity | null;
  auth?: ParentAuth;
}) {
  const t = PARENT_COPY[lang];
  const [openDay, setOpenDay] = useState<string | null>(null);
  /** The whole register, on top of the screen. */
  const [showAll, setShowAll] = useState(false);

  /*
   * ⚠️ THE CLOCK IS READ ONCE PER MOUNT. Every date on this screen is derived
   * from it, and a component that called `new Date()` in its body could put two
   * different "todays" on one grid across a midnight.
   */
  const now = useMemo(() => new Date(), []);

  /*
   * 🔴 THE OLDEST DAY THE ANSWER ACTUALLY COVERS, READ OFF THE ANSWER.
   *
   * The server clamps `days` and states the window it used, so this is the only
   * honest floor for the month arrows: a month before it is a month nobody was
   * asked about, and its squares must draw as unknown rather than as "no
   * register was taken". `a.days` is oldest-first; the fallback keeps a server
   * that sends no `days` working exactly as it did.
   */
  const windowStart = useMemo(
    () => a.days?.[0]?.day ?? dayKey(shiftDays(now, -(a.windowDays - 1))),
    [a.days, a.windowDays, now],
  );

  /** The months a parent may open — every one the window reaches, newest first. */
  const months = useMemo(
    () => monthsInWindow({ windowStart, today: now }),
    [windowStart, now],
  );

  /*
   * Which month is on screen. It opens on this one, which is the month a parent
   * came to look at.
   */
  const [view, setView] = useState(() => ({
    year: now.getFullYear(),
    month: now.getMonth(),
  }));

  const viewIndex = months.findIndex(
    (m) => m.year === view.year && m.month === view.month,
  );
  /* `months` is newest first, so "earlier" is forwards through it. */
  const canGoBack = viewIndex >= 0 && viewIndex < months.length - 1;
  const canGoForward = viewIndex > 0;

  const goMonth = (step: number) => {
    const next = months[viewIndex + step];
    if (!next) return;
    setView({ year: next.year, month: next.month });
    /* The open dialogue is about a day in the month being left. */
    setOpenDay(null);
  };

  /*
   * 🔴 ESCAPE CLOSES IT, BECAUSE IT IS A DIALOGUE NOW. A layer over the page
   * that only a specific button dismisses is the failure the panel-under-the-
   * grid shape was chosen to avoid; this is the keyboard half of paying that
   * back. The backdrop is the pointer half, and `DayDetail`'s own close button
   * was already there.
   */
  useEffect(() => {
    if (openDay === null && !showAll) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      /* The day detail is opened *from* the list and sits on top of it, so one
         Escape closes the topmost layer rather than both at once. */
      if (openDay !== null) setOpenDay(null);
      else setShowAll(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [openDay, showAll]);
  /*
   * ⚠️ STICKY, RATHER THAN `openDay !== null`. `useResource` lists `enabled` in
   * its effect's dependencies, so a flag that went false again on close would
   * abort and re-issue the same request every time a parent opened a second day
   * — twenty-eight taps, twenty-eight identical reads of one class's homework.
   * Once is enough; the list does not change while the screen is open.
   */
  const byDay = useMemo(() => dayIndex(activity?.days ?? []), [activity]);
  const today = useMemo(() => istToday(), []);

  /*
   * The month on screen.
   *
   * ⚠️ MEMOISED ON THE ANSWER AND THE MONTH, NOT ON THE CLOCK. Without this it
   * would rebuild thirty-one objects on every render of a screen that animates.
   */
  const month = useMemo(
    () =>
      attendanceMonth({
        year: view.year,
        month: view.month,
        absences: a.absences.map((row) => row.day),
        states: a.days,
        windowStart,
        lang,
        today: now,
      }),
    [a.absences, a.days, view.year, view.month, windowStart, lang, now],
  );

  /*
   * The response's whole window as one list — what "View all attendance" opens.
   *
   * ⚠️ BUILT SEPARATELY FROM THE MONTH, because the month is whatever the arrows
   * have moved to, and a list titled "all" that quietly showed August would be
   * the same broken promise the dead button was.
   */
  const fullCalendar = useMemo(
    () =>
      attendanceCalendar({
        windowDays: a.windowDays,
        absences: a.absences.map((row) => row.day),
        states: a.days,
        lang,
        today: now,
      }),
    [a.windowDays, a.absences, a.days, lang, now],
  );

  /*
   * 🔴 THE LABEL IS THE MONTH BEING DRAWN, NOT "Last 28 days".
   *
   * The chip that stood here was a `<div>` with `cursor-pointer` and nothing
   * behind it, over a grid that was always the last four weeks whatever the
   * arrows did. Both are now the same fact — the month — so the heading, the
   * ring, the counts and the squares cannot disagree with each other.
   */
  const windowLabel = month.label;
  /** The same window as the tail of a sentence — "in August 2026". */
  const windowPhrase = `in ${month.label}`;

  /*
   * 🔴 THE FOUR STATES, COUNTED — AND `NO_REGISTER` IS ONE OF THEM.
   *
   * `AttendanceDay` says the server has no `presentDays` and no `rate`, and it
   * is right: `Absence` records absences only. But `attendanceCalendar` already
   * resolves a state for **every** day in the window, so counting its squares is
   * reading what is on screen rather than inventing a figure behind it.
   *
   * ⚠️ THE DENOMINATOR IS THE WHOLE WINDOW, WHICH IS WHY "no record" GETS A ROW
   * AND A PERCENTAGE OF ITS OWN. `AttendanceState` warns that folding
   * `NO_REGISTER` into `PRESENT` would put a green square up for a day nobody
   * checked — *"the single dishonesty this whole screen is arranged to
   * prevent"*. Counted separately and shown separately, a 96% means "96% of 28
   * days, and here are the other two states"; hidden, the same number would be a
   * claim about a child that no register supports.
   *
   * 🔴 AND THE DENOMINATOR IS `countable`, NOT THE MONTH. Open August on the
   * 12th and nineteen of its squares have not happened yet; dividing by 31 would
   * print "61% no register" about days the school has not reached. `MonthDay`
   * carries `future` and `outsideWindow` for exactly this line.
   */
  const tally = useMemo(() => {
    let present = 0;
    let absent = 0;
    let holiday = 0;
    let noRecord = 0;
    for (const d of month.countable) {
      if (d.state === "PRESENT") present += 1;
      else if (d.state === "ABSENT") absent += 1;
      else if (d.state === "HOLIDAY") holiday += 1;
      else noRecord += 1;
    }
    const total = month.countable.length;
    const pct = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));
    return {
      present,
      absent,
      holiday,
      noRecord,
      total,
      presentPct: pct(present),
      absentPct: pct(absent),
      noRecordPct: pct(noRecord),
    };
  }, [month]);

  /*
   * The most recent days in this month that actually say something, newest
   * first.
   *
   * ⚠️ `NO_REGISTER` DAYS ARE SKIPPED. A row reading "no register taken" is the
   * absence of a fact, and a table of them would bury the days that carry one —
   * which is the same reason the grid gives those squares no entrance animation
   * and no accessible name.
   */
  const recentDays = useMemo(
    () =>
      [...month.countable]
        .reverse()
        .filter((d) => d.state !== "NO_REGISTER")
        .slice(0, 5),
    [month],
  );

  /** Every day the response can speak to, newest first — the "all" list. */
  const allDays = useMemo(
    () =>
      [...fullCalendar.days, ...fullCalendar.earlier]
        .filter((d) => d.state !== "NO_REGISTER")
        .sort((x, y) => (x.key < y.key ? 1 : -1)),
    [fullCalendar],
  );

  /*
   * The one line of context a row can carry, and it is a recorded one.
   *
   * 🔴 NO INVENTED CHECK-IN TIME AND NO INVENTED REASON. This column used to
   * read "Checked in at 09:05 AM" on any present day and "Sick leave" on any
   * absent one — neither is stored anywhere, and a parent taking "sick leave" to
   * the school as the register's word is precisely the harm the rest of this
   * screen is arranged around. A day with nothing recorded beside it says so.
   */
  const remarkFor = (d: CalendarDay): string => {
    if (d.holidayName !== null) return d.holidayName;
    const play = byDay.get(d.key);
    if (play && (play.sessions > 0 || play.events > 0))
      return t.dayPlaySummary(play.sessions, play.minutes);
    return "—";
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-line bg-primary-tint text-primary shadow-2xs">
              <CalendarIcon size={19} />
            </span>
            <div>
              <h1 className="font-dash text-dash-xl font-black text-ink">Attendance</h1>
              <p className="font-dash text-dash-xs text-muted font-bold">{a.childName}</p>
            </div>
          </div>
        </div>

        {/* Top Section: Overview & Calendar */}
        <div className="grid items-start gap-6 lg:grid-cols-2">
          {/* Left Column: Stats Cards & Overview */}
          <div className="flex flex-col gap-6">
            {/*
              🔴 TWO UP ON A PHONE TOO, NOT ONE. `grid-cols-1` below `sm` gave
              each of these tiles the full width of the screen to hold a single
              number — "0" under "Days absent" — and stacked them, so 300px of a
              740px phone went on two figures and the CALENDAR THEY INTRODUCE
              started below the fold. The overview's own stat row is `grid-cols-2`
              at every width and reads well at 360px; this matches it.
            */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {/* Stat Card 1: Days Absent */}
              <div className="rounded-2xl border border-rose-100 bg-[#FFF8F6] p-5 shadow-xs flex flex-col justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-pill-danger-line bg-pill-danger-bg text-pill-danger-fg">
                    <CalendarIcon size={14} />
                  </span>
                  <span className="font-dash text-dash-xs font-extrabold text-orange-600">
                    Days absent
                  </span>
                </div>
                <div>
                  {/* ⚠️ THE GRID'S COUNT, NOT `a.absentDays`. The tile and the
                      squares beside it have to be the same window — the arrows
                      move the grid to another month, and a server figure held
                      fixed under a caption reading "July 2026" is the
                      disagreement `AttendanceCalendar.earlier` exists to
                      prevent. */}
                  <span className="font-dash text-4xl font-black text-orange-600 tabular">
                    {tally.absent}
                  </span>
                </div>
                <p className="font-dash text-[11px] font-semibold text-slate-400">
                  {windowLabel}
                </p>
              </div>

              {/* Stat Card 2: Last Absence */}
              <div className="rounded-2xl border border-emerald-100 bg-[#F2FAF5] p-5 shadow-xs flex flex-col justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">
                    ✓
                  </div>
                  <span className="font-dash text-dash-xs font-extrabold text-emerald-700">
                    Last absence
                  </span>
                </div>
                <div>
                  <span className="font-dash text-2xl font-black text-emerald-900 tabular">
                    {a.lastAbsence ? formatDay(a.lastAbsence) : "Never absent"}
                  </span>
                </div>
                {/* ⚠️ NOT THE PICKER'S WINDOW. `lastAbsence` is the most recent
                    of all time and "may be older than the window" — labelling
                    it with a range would date an absence to a fortnight it did
                    not happen in. */}
                <p className="font-dash text-[11px] font-semibold text-slate-400">
                  All time
                </p>
              </div>
            </div>

            {/* Attendance Overview Card */}
            <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-6 shadow-xs flex flex-col justify-between gap-5">
              {/* ⚠️ STACKED ON A PHONE. A heading and a month picker sharing one
                  `justify-between` row have 360px between them, which is not
                  enough for either: the heading broke to "Attendance / Overview"
                  and the picker beside it broke to "August / 2026" — two controls
                  each wrapped so the other could fit, and neither fitting. Given
                  its own line each is one line. */}
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <h3 className="font-dash text-dash-md font-extrabold text-ink">
                  Attendance Overview
                </h3>
                <RangeSelect
                  value={`${String(view.year)}-${String(view.month)}`}
                  label={windowLabel}
                  options={months}
                  onPick={(choice) => {
                    setView(choice);
                    setOpenDay(null);
                  }}
                />
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <AttendanceRing tally={tally} />

                <ul className="min-w-[13rem] flex-1 space-y-2.5 font-dash text-dash-xs">
                  {(
                    [
                      ["present", "Present Days", tally.present, tally.presentPct, "#22C55E"],
                      ["absent", "Absent Days", tally.absent, tally.absentPct, "#F43F5E"],
                      ["norecord", "No Record", tally.noRecord, tally.noRecordPct, "#CBD5E1"],
                    ] as const
                  ).map(([key, label, count, pct, colour]) => (
                    <li key={key} className="flex items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: colour }}
                      />
                      <span className="flex-1 font-bold text-slate-600">{label}</span>
                      <span className="tabular font-black text-slate-900">{count}</span>
                      <span className="tabular font-bold text-slate-400 w-10 text-right">{pct}%</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Bottom Banner with Thumbs Up & Mountains Sun Graphic */}
              <div className="rounded-xl border border-emerald-100 bg-[#F0FDF4] p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-pill-success-line bg-pill-success-bg text-pill-success-fg">
                    <CheckIcon size={15} />
                  </span>
                  <p className="font-dash text-dash-xs font-bold text-emerald-900 leading-snug">
                    Great! {a.childName} has been present {tally.presentPct}% of the time {windowPhrase}.
                  </p>
                </div>

                {/* SVG Graphic: Green hills with glowing yellow sun */}
                <div className="hidden sm:block shrink-0">
                  <svg viewBox="0 0 90 45" className="w-16 h-9">
                    <circle cx="72" cy="14" r="8" fill="#FFD028" />
                    <path d="M 0 45 Q 22 22 45 45 Z" fill="#86EFAC" opacity="0.9" />
                    <path d="M 25 45 Q 58 12 90 45 Z" fill="#22C55E" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Attendance Calendar Card */}
          <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-6 shadow-xs flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-dash text-dash-md font-extrabold text-ink">
                Attendance Calendar
              </h3>
              <div className="flex items-center gap-2">
                <RangeSelect
                  value={`${String(view.year)}-${String(view.month)}`}
                  label={windowLabel}
                  options={months}
                  onPick={(choice) => {
                    setView(choice);
                    setOpenDay(null);
                  }}
                />
                {/*
                 * ⚠️ A MONTH AT A TIME, AND CLAMPED TO THE RESPONSE. `months` is
                 * built from `windowStart` — the oldest day the answer actually
                 * covers — so the arrows walk the months this response holds and
                 * then stop. Stepping past them would fill a grid with squares
                 * for days the server was never asked about.
                 */}
                <div className="flex items-center gap-1 text-slate-400">
                  <button
                    type="button"
                    aria-label="Previous month"
                    disabled={!canGoBack}
                    onClick={() => {
                      goMonth(1);
                    }}
                    className="h-[44px] w-[44px] pointer-fine:h-7 pointer-fine:w-7 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 font-bold disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    aria-label="Next month"
                    disabled={!canGoForward}
                    onClick={() => {
                      goMonth(-1);
                    }}
                    className="h-[44px] w-[44px] pointer-fine:h-7 pointer-fine:w-7 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 font-bold disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>

            <div>
              <AttendanceMonthGrid
                month={month}
                lang={lang}
                selected={openDay}
                onPick={(day) => {
                  setOpenDay((current) => (current === day ? null : day));
                }}
              />
              {tally.total > 0 && tally.absent === 0 && (
                <p className="mt-4 text-dash-md text-ink">{t.attendanceNone}</p>
              )}
              {openDay === null && (
                <p className="mt-3 text-dash-xs text-slate-400 font-semibold text-center">
                  Tap on a day to see what happened
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Day Detail Modal */}
        {openDay !== null && (
          <div
            onMouseDown={() => {
              setOpenDay(null);
            }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={t.dayPick}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              className="max-h-[85dvh] w-full max-w-lg overflow-auto"
            >
              <DayDetail
                dayKey={openDay}
                lang={lang}
                today={today}
                activity={byDay.get(openDay)}
                /* ⚠️ LOOKED UP IN THE FULL WINDOW, NOT THE PAGED ONE. A day
                   opened from the "all attendance" list is very often outside
                   whatever the picker has narrowed the grid to, and finding
                   nothing there would show a real absence as "no register". */
                state={fullCalendar.days.find((d) => d.key === openDay)?.state ?? null}
                holidayName={
                  fullCalendar.days.find((d) => d.key === openDay)?.holidayName ?? null
                }
                onClose={() => {
                  setOpenDay(null);
                }}
              />
            </div>
          </div>
        )}

        {/* Bottom Section: Recent Activity & Attendance Note */}
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {/* Recent Attendance Activity Card */}
          <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-6 shadow-xs flex flex-col justify-between gap-4 min-w-0">
            {/* ⚠️ "Recent" WOULD BE A LIE THE MOMENT THE ARROWS MOVED. This
                table is the last five recorded days OF THE MONTH ON SCREEN, so
                it names that month — the same reason the chip above stopped
                saying "Last 28 days". */}
            <h3 className="font-dash text-dash-md font-extrabold text-ink">
              Latest in {month.label}
            </h3>

            {recentDays.length === 0 ? (
              <p className="font-dash text-dash-xs text-muted">
                {t.attendanceNoRows}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[28rem] border-collapse font-dash text-dash-xs">
                  <thead>
                    <tr className="text-left text-slate-400 font-bold border-b border-slate-100">
                      <th className="pb-2.5">Date</th>
                      <th className="pb-2.5">Status</th>
                      <th className="pb-2.5">Remarks</th>
                      <th className="pb-2.5 w-6"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recentDays.map((d) => {
                      const remark = remarkFor(d);
                      return (
                        <tr key={d.key} className="hover:bg-slate-50/60 transition-colors">
                          <td className="py-3 font-extrabold text-slate-800">
                            {formatDay(d.key)}
                          </td>
                          <td className="py-3">
                            <AttendanceStatusPill state={d.state} lang={lang} />
                          </td>
                          <td className="py-3 text-slate-500 font-medium">
                            {remark}
                          </td>
                          {/* 🔴 THE CHEVRON IS THE CONTROL, NOT THE ROW. A `<tr
                              onClick>` is unreachable by keyboard and announces
                              itself as nothing — the same failure the grid's
                              squares were turned into real buttons to fix. */}
                          <td className="py-3 text-right">
                            <button
                              type="button"
                              aria-label={d.label}
                              onClick={() => {
                                setOpenDay(d.key);
                              }}
                              className="-my-2.5 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg px-1 font-bold text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
                            >
                              ›
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="pt-2 text-center border-t border-slate-50">
              <button
                type="button"
                aria-haspopup="dialog"
                disabled={allDays.length === 0}
                onClick={() => {
                  setShowAll(true);
                }}
                className="min-h-[44px] rounded-full border border-pink-200 bg-white px-5 py-2 font-dash text-dash-xs font-bold text-pink-600 hover:bg-pink-50 transition-colors inline-flex items-center gap-1.5 shadow-2xs disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                View All Attendance  →
              </button>
            </div>
          </div>

          {/* Attendance Note Card */}
          <div className="rounded-2xl border border-amber-100 bg-[#FFFDF5] p-4 sm:p-6 shadow-xs flex flex-col justify-between gap-4">
            {/* Top 3D Calendar Illustration with ribbon checkmark and leaves */}
            <div className="flex justify-center my-1">
              <svg viewBox="0 0 120 95" className="w-28 h-22">
                <path d="M 18 55 C 8 38 22 18 38 28 C 32 42 28 50 18 55 Z" fill="#4ADE80" />
                <path d="M 102 55 C 112 38 98 18 82 28 C 88 42 92 50 102 55 Z" fill="#4ADE80" />
                <rect x="36" y="18" width="48" height="52" rx="10" fill="#FFFFFF" stroke="#F43F5E" strokeWidth="2.5" />
                <rect x="36" y="18" width="48" height="15" rx="7" fill="#F43F5E" />
                <circle cx="47" cy="16" r="2.5" fill="#CBD5E1" />
                <circle cx="73" cy="16" r="2.5" fill="#CBD5E1" />
                <circle cx="60" cy="48" r="13" fill="#EF4444" />
                <path d="M 53 48 L 58 53 L 68 42" fill="none" stroke="#FFFFFF" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div className="space-y-1 text-center">
              <h3 className="font-dash text-dash-md font-extrabold text-amber-950">
                Attendance Note
              </h3>
              <p className="font-dash text-dash-xs text-amber-900/80 leading-relaxed font-semibold">
                Regular attendance helps children build better learning habits.
              </p>
            </div>

            <div className="rounded-xl border border-amber-200/60 bg-amber-50/80 p-3.5 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 font-dash text-dash-xs font-bold text-amber-900">
                <SparkleIcon size={16} />
                <span>Tip for Parents</span>
              </div>
              <p className="font-dash text-[11px] text-amber-800 font-semibold leading-relaxed">
                Ensure {a.childName} attends school regularly and on time for the best learning experience.
              </p>
            </div>
          </div>
        </div>

        {/*
         * The whole register, which is what the button under the table has been
         * offering since it was drawn.
         *
         * ⚠️ THE RESPONSE'S WINDOW, NOT "EVERY DAY EVER". `fullCalendar` is
         * `a.windowDays` long because that is all the server sends, and the
         * footer says so rather than letting "all" imply a history that was
         * never fetched. `earlier` rides along: those are absences the server
         * knows about from before the window, and dropping them from a list
         * called "all" would be the same quiet lie.
         */}
        {showAll && (
          <div
            onMouseDown={() => {
              setShowAll(false);
            }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="All attendance"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              className="flex max-h-[85dvh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl border border-slate-100 bg-white shadow-2xl sm:rounded-2xl"
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div>
                  <h3 className="font-dash text-dash-md font-extrabold text-ink">
                    All Attendance
                  </h3>
                  <p className="font-dash text-[11px] font-semibold text-slate-400">
                    {a.childName} · {allDays.length} recorded days
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowAll(false);
                  }}
                  className="h-8 w-8 shrink-0 rounded-lg border border-slate-200 font-bold text-slate-500 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="overflow-auto px-5 py-3">
                <table className="w-full min-w-[24rem] border-collapse font-dash text-dash-xs">
                  <thead>
                    <tr className="text-left text-slate-400 font-bold border-b border-slate-100">
                      <th className="pb-2.5">Date</th>
                      <th className="pb-2.5">Status</th>
                      <th className="pb-2.5">Remarks</th>
                      <th className="pb-2.5 w-6"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {allDays.map((d) => (
                      <tr key={d.key} className="hover:bg-slate-50/60 transition-colors">
                        <td className="py-3 font-extrabold text-slate-800">
                          {formatDay(d.key)}
                        </td>
                        <td className="py-3">
                          <AttendanceStatusPill state={d.state} lang={lang} />
                        </td>
                        <td className="py-3 text-slate-500 font-medium">
                          {remarkFor(d)}
                        </td>
                        <td className="py-3 text-right">
                          <button
                            type="button"
                            aria-label={d.label}
                            onClick={() => {
                              setShowAll(false);
                              setOpenDay(d.key);
                            }}
                            className="-my-2.5 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg px-1 font-bold text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
                          >
                            ›
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="border-t border-slate-100 px-5 py-3 font-dash text-[11px] font-semibold text-slate-400">
                Days nobody took a register on are left out. The school sends the
                last {a.windowDays} days, plus any earlier absence on record.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────── C4 ── */

/**
 * C4 — the unified feed, *"each item tagged by channel"*.
 *
 * ⚠️ IT RETURNS AN EMPTY LIST TODAY, AND THE SCREEN SAYS SO IN A SENTENCE
 * rather than showing blank space. §9.2 owns `send_outbox`; the backend reads
 * through a narrow port with an empty binding, so this calls a real endpoint
 * and renders the real answer. The alternatives were a second `Notification`
 * table — the exact duplicate the outbox exists to prevent — or a fixture.
 * 📋 Reported.
 */
export function NotificationsScreen({ lang = "en", auth }: Base) {
  const t = PARENT_COPY[lang];
  const resource = useResource<ParentNotification[]>(
    (signal) => getNotifications({ ...auth, signal }),
    [],
    { isEmpty: (rows) => rows.length === 0 },
  );

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead title={t.notifications} />
      <Surface
        resource={resource}
        lang={lang}
        skeletonRows={4}
        emptyMessage={t.noNotifications}
      >
        {(rows) => (
          /* ⚠️ EACH MESSAGE IS ITS OWN CARD. As `flex-wrap` list items on a
             phone, a channel pill, a template name and a date wrapped into each
             other and two consecutive messages became one paragraph. */
          <ul className="flex flex-col gap-3">
            {rows.map((n) => (
              <li key={n.id}>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface p-4 shadow-card">
                  <span className="flex flex-wrap items-center gap-3">
                    <StatusPill
                      tone="neutral"
                      icon={
                        n.channel === "WHATSAPP" ? (
                          <InfoIcon size={14} />
                        ) : (
                          <ChildrenIcon size={14} />
                        )
                      }
                    >
                      {n.channel === "WHATSAPP"
                        ? t.channelWhatsapp
                        : t.channelInApp}
                    </StatusPill>
                    <span className="font-bold text-ink">{n.template}</span>
                  </span>
                  <span className="text-dash-xs text-muted">
                    {new Date(n.occurredAt).toLocaleDateString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Surface>
    </section>
  );
}

/* ────────────────────────────────────────────────────── the consent screen ── */

export interface ConsentScreenProps extends Base {
  playerId: string;
  onErased?: (result: ErasureResult) => void;
}

/**
 * 🔴 THE EXPLICIT CONSENT ACTION — contract D1, and the screen OD-50 was
 * waiting for.
 *
 * *"After OTP verification the parent performs an explicit consent action — a
 * screen with a real decision, never a side effect of logging in."* Signing in
 * is not consenting: a row written by the login handler would document a
 * decision nobody made while turning retention on, which is the *"false green"*
 * OD-50 refused. So this is reached deliberately, and declining is a real
 * option that costs the child nothing — *"a child may always play"*.
 *
 * It also carries P1, P2 and P3, because settings-and-controls.md puts consent
 * management in C5 *"not buried"*, and a parent looking at their consent record
 * is exactly the parent who wants the two buttons beside it.
 */
export function ConsentScreen({
  playerId,
  lang = "en",
  auth,
  onErased,
}: ConsentScreenProps) {
  const t = PARENT_COPY[lang];
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [confirmingErase, setConfirmingErase] = useState(false);
  const [version, setVersion] = useState(0);

  const resource = useResource<ConsentView>(
    (signal) => getConsent(playerId, { ...auth, signal }),
    [playerId, version],
  );

  const run = async (work: () => Promise<string | null>): Promise<void> => {
    setBusy(true);
    try {
      setNote(await work());
      // Re-reads the record rather than patching local state: the screen's job
      // is to show what is stored, and a locally-optimistic consent state is
      // the one thing on this screen that must never be optimistic.
      setVersion((n) => n + 1);
    } catch {
      setNote(t.loadFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead title={t.consentTitle} />
      <div>
        <Surface resource={resource} lang={lang} skeletonRows={3}>
          {(c) => (
            <>
              {/*
                ✅ THREE CARDS: THE DECISION, THE EXPORT, THE ERASURE. They were
                one column separated by `<hr>` rules, which on a phone reads as
                one long form — and two of the three sections end in an
                irreversible button. P1 and P2 are different rights with
                different consequences, and the layout now says so before the
                copy does.
              */}
              <Panel>
                <p>
                  <StatusPill
                    tone={consentTone(c.status)}
                    icon={<PrivacyIcon size={14} />}
                  >
                    {consentLabel(c.status, lang)}
                  </StatusPill>
                </p>

                {c.status === "GRANTED" ? (
                  <dl className="mt-4 grid max-w-md grid-cols-2 gap-1 text-dash-xs">
                    <dt className="text-muted">{t.consentGrantedOn}</dt>
                    <dd>
                      {c.grantedAt
                        ? new Date(c.grantedAt).toLocaleDateString()
                        : "—"}
                    </dd>
                    <dt className="text-muted">{t.consentUntil}</dt>
                    <dd>
                      {c.retainUntil
                        ? new Date(c.retainUntil).toLocaleDateString()
                        : "—"}
                    </dd>
                    <dt className="text-muted">{t.consentVerifiedBy}</dt>
                    {/* P3 — "the record must be inspectable by the person who
                      granted it", and HOW it was verified is the field that
                      makes it a record rather than a checkbox. */}
                    <dd>
                      {c.verificationMethod === PARENT_VERIFICATION
                        ? t.consentVerifiedPhoneOtp
                        : (c.verificationMethod ?? "—")}
                    </dd>
                  </dl>
                ) : (
                  <>
                    <p className="mt-4 max-w-prose">{t.consentAsk}</p>
                    <p className="mt-2 max-w-prose text-muted">
                      {t.consentAskDetail}
                    </p>
                  </>
                )}

                <div className="mt-6 flex flex-wrap gap-2">
                  {c.status !== "GRANTED" && (
                    <DashButton
                      variant="primary"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          /*
                           * 🔴 THE INTENT IS BUILT AND CHECKED BEFORE ANYTHING IS
                           * WRITTEN, in the vocabulary §7 already defined
                           * (`ConsentIntent` / `mayRetain`). This parent is on
                           * their own phone, having proved control of the number
                           * the roster recorded — which is what `'phone-otp'`
                           * means and what OD-50 was waiting for.
                           */
                          if (!mayWriteConsent(retentionIntent(Date.now())))
                            return null;
                          /*
                           * ⚠️ P3-31 — the server now needs the notice version.
                           * This legacy screen is not mounted for any route (the
                           * console registry serves every parent route, and its
                           * Privacy view SHOWS the notice before granting); it
                           * reads the current version so it still compiles to a
                           * valid request rather than a 400.
                           */
                          const notice = await getConsentNotice(playerId, auth);
                          await grantConsent(playerId, notice.version, auth);
                          return null;
                        })
                      }
                    >
                      {t.consentGive}
                    </DashButton>
                  )}
                  {c.status === "GRANTED" && (
                    <DashButton
                      variant="destructive"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await revokeConsent(playerId, auth);
                          return t.revoked;
                        })
                      }
                    >
                      {t.revoke}
                    </DashButton>
                  )}
                </div>

                {/* 🔴 THE LIMITATION, STATED PLAINLY AND ALWAYS. Revoking sets a
                  date; `resolveRecording` reads it at the START of the next
                  session, and the historical rows go when the retention job
                  runs. A revoke button that implies instant deletion is a right
                  that exists on paper. */}
                <p className="mt-3 max-w-prose text-dash-xs text-muted">
                  {t.revokeWarning}
                </p>
              </Panel>

              {/* P1 */}
              <SectionHead>{t.exportTitle}</SectionHead>
              <Panel>
                <p className="max-w-prose text-muted">{t.exportHelp}</p>
                <div className="mt-4">
                  <DashButton
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        downloadExport(
                          playerId,
                          await exportChildData(playerId, auth),
                        );
                        return t.exportDone;
                      })
                    }
                  >
                    <DownloadIcon size={16} />
                    {t.exportAction}
                  </DashButton>
                </div>
              </Panel>

              {/* P2 */}
              <SectionHead>{t.eraseTitle}</SectionHead>
              <Panel>
                <p className="max-w-prose text-muted">{t.eraseHelp}</p>
                {/* 🔴 SAYS WHAT SURVIVES. The Player row stays — it is the
                    school's enrolment record, and a parent who is not told that
                    will believe we kept something we should not have. It is a
                    `Note` rather than a third grey paragraph because it is the
                    sentence that decides whether the button below is understood. */}
                <div className="mt-3 max-w-prose">
                  <Note tone="neutral" icon={<InfoIcon size={16} />}>
                    {t.eraseWhatRemains}
                  </Note>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {confirmingErase ? (
                    <>
                      <DashButton
                        variant="destructive"
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            const result = await eraseChildData(playerId, auth);
                            setConfirmingErase(false);
                            onErased?.(result);
                            return t.eraseDone(result.deleted.events);
                          })
                        }
                      >
                        {t.eraseConfirm}
                      </DashButton>
                      <DashButton
                        variant="ghost"
                        onClick={() => setConfirmingErase(false)}
                      >
                        {t.eraseCancel}
                      </DashButton>
                    </>
                  ) : (
                    /* Two taps, because this one is irreversible. The confirm is
                     a second button rather than a `window.confirm`, which is
                     unstyleable and untranslatable. */
                    <DashButton
                      variant="destructive"
                      onClick={() => setConfirmingErase(true)}
                    >
                      {t.eraseAction}
                    </DashButton>
                  )}
                </div>
              </Panel>

              {note && (
                <p className="mt-4">
                  <StatusPill tone="neutral" icon={<CheckIcon size={14} />}>
                    {note}
                  </StatusPill>
                </p>
              )}
            </>
          )}
        </Surface>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── C5 ── */

export interface SettingsScreenProps extends Base {
  /** Null for a parent with no linked children — the account half still works. */
  playerId: string | null;
  onOpenConsent: () => void;
}

/**
 * C5 — *"WhatsApp toggle · language · child data & consent · children list"*,
 * and settings-and-controls.md's *"consent management lives here, not buried"*.
 *
 * 🔴 SPLIT INTO AN ACCOUNT HALF AND A PER-CHILD HALF, because P8 requires
 * *"settings must be per-child not per-account"* while quiet hours are an
 * account concern (one phone, one bedtime). The two halves save independently:
 * a body carrying every field would let changing the language clear the quiet
 * hours the parent set last month.
 *
 * ✅ AND IT IS NOW THE PARENT TIER'S *ONLY* SETTINGS SCREEN, WHICH IS WHY IT
 * OPENS WITH A SENTENCE ABOUT SIGNING IN.
 *
 * 🔴 A PARENT WAS SHOWN THE WORD TWICE, POINTING AT TWO PLACES. This screen sat
 * in the nav bar as "Alerts" — a name nothing else in the product used — while
 * the shell's own Settings control, two rows below it, opened G1: a change-your-
 * password form and an authenticator app. Neither can work for this tier. §9.1
 * signs a parent in with a phone number and a one-time code; `/auth/login`
 * refuses PARENT outright and `changeOwnPassword` refuses a row with no
 * `passwordHash`, so every submission of that form would answer *"that password
 * did not match"* about a password that has never existed.
 *
 * ⚠️ SO THE FIX IS NOT A HIDDEN FORM — IT IS A STATED FACT. `routes.ts` sends
 * this tier's Settings control here and bounces a parent off G1–G4, and the
 * first section on this page says there is no password and who to tell when the
 * number changes. A missing control that is explained is answered; a missing
 * control that is silent is searched for.
 */
export function SettingsScreen({
  playerId,
  lang = "en",
  auth,
  onOpenConsent,
}: SettingsScreenProps) {
  const t = PARENT_COPY[lang];
  const [version, setVersion] = useState(0);
  const [saved, setSaved] = useState(false);
  /*
   * ⚠️ G3'S SWITCH, CARRIED HERE FOR THE ONE TIER THAT NO LONGER PASSES THROUGH
   * G3. `useReduceMotion.ts` applies it everywhere and says it is *"surfaced on
   * G3 Accessibility"*; sending a parent to their own settings instead would
   * have quietly taken the setting away from the tier with the most movement on
   * screen (`PlayfulUI`). It is not an account preference and is not sent to the
   * server — same store, same browser key, drawn in the place a parent looks.
   */
  const [reduceMotion, setReduceMotion] = useState(getReduceMotionSetting);

  const account = useResource<AccountPreferences>(
    (signal) => getAccountPreferences({ ...auth, signal }),
    [version],
  );
  const child = useResource<ChildPreferences>(
    (signal) => getChildPreferences(playerId ?? "", { ...auth, signal }),
    [playerId, version],
    { enabled: playerId !== null },
  );

  const reload = (): void => {
    setSaved(true);
    setVersion((n) => n + 1);
  };

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead title={t.settings} subtitle={t.settingsIntro} />

      {/*
        🔴 FIRST, BECAUSE IT IS THE QUESTION A SETTINGS SCREEN IS OPENED WITH.
        "Where do I change my password" has an answer here — there isn't one —
        and a screen that leaves it out is a screen a parent reads twice looking
        for the control. It is a `Note` rather than a `Panel` because nothing in
        it is a control: it states how the account works and who to tell when the
        number changes, and a bordered card full of prose reads as a form that
        failed to load its fields.
      */}
      <SectionHead>{t.signInSection}</SectionHead>
      <Note
        tone="info"
        icon={<PrivacyIcon size={16} />}
        detail={t.signInNumber}
      >
        {t.signInHow}
      </Note>

      <SectionHead>{t.accountSettings}</SectionHead>
      <div>
        <Surface resource={account} lang={lang} skeletonRows={4}>
          {(a) => (
            <Panel>
              <div className="flex max-w-md flex-col gap-6">
                {/* D3 — the messaging opt-in. ⚠️ ADR-016: this touches
                  ParentalConsent in neither direction.

                  ⚠️ `-mx-3` PULLS THE ROW'S OWN PADDING BACK TO THE CARD'S EDGE,
                  so the pressable area extends past the text without the label
                  appearing indented against the controls above it. */}
                <div className="-mx-3">
                  <CheckboxRow
                    checked={a.whatsappOptIn}
                    onChange={(whatsappOptIn) =>
                      void saveAccountPreferences({ whatsappOptIn }, auth).then(
                        reload,
                      )
                    }
                  >
                    {t.whatsappOptIn}
                  </CheckboxRow>
                </div>

                {/* P5 — quiet hours. The wrap-midnight case is the ordinary one,
                  so the screen names it rather than leaving the parent to
                  wonder whether 21:00–07:00 means anything. */}
                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-dash-xs font-bold text-muted">
                    {t.quietHours}
                  </legend>
                  <div className="flex flex-wrap items-end gap-3">
                    <MinuteInput
                      label={t.quietFrom}
                      value={a.quietHours?.quietFrom ?? null}
                      onChange={(quietFrom) =>
                        void saveAccountPreferences(
                          {
                            quietHours:
                              quietFrom === null
                                ? null
                                : {
                                    quietFrom,
                                    quietTo: a.quietHours?.quietTo ?? 420,
                                  },
                          },
                          auth,
                        ).then(reload)
                      }
                    />
                    <MinuteInput
                      label={t.quietTo}
                      value={a.quietHours?.quietTo ?? null}
                      onChange={(quietTo) =>
                        void saveAccountPreferences(
                          {
                            quietHours:
                              quietTo === null
                                ? null
                                : {
                                    quietFrom: a.quietHours?.quietFrom ?? 1260,
                                    quietTo,
                                  },
                          },
                          auth,
                        ).then(reload)
                      }
                    />
                  </div>
                  {a.quietHours === null ? (
                    <span className="text-dash-xs text-muted">
                      {t.quietHoursOff}
                    </span>
                  ) : (
                    a.quietHours.quietFrom !== null &&
                    a.quietHours.quietTo !== null &&
                    a.quietHours.quietFrom > a.quietHours.quietTo && (
                      <span className="text-dash-xs text-muted">
                        {t.quietWrapsMidnight}
                      </span>
                    )
                  )}
                </fieldset>

                {/* P5 — granularity. "One on/off switch means an over-messaged
                  parent turns off everything, including the weekly report that
                  drives renewal." */}
                <fieldset className="-mx-3">
                  <legend className="mb-1 px-3 text-dash-xs font-bold text-muted">
                    {t.messageKinds}
                  </legend>
                  {a.kinds.map((k) => (
                    <CheckboxRow
                      key={k.kind}
                      checked={k.enabled}
                      onChange={(enabled) =>
                        void saveAccountPreferences(
                          { kinds: [{ kind: k.kind, enabled }] },
                          auth,
                        ).then(reload)
                      }
                    >
                      {kindLabel(k.kind, lang)}
                    </CheckboxRow>
                  ))}
                </fieldset>
              </div>
            </Panel>
          )}
        </Surface>
      </div>

      {playerId !== null && (
        <>
          <SectionHead>{t.childSettings}</SectionHead>
          <div>
            <Surface resource={child} lang={lang} skeletonRows={3}>
              {(c) => (
                <Panel>
                  <div className="flex max-w-md flex-col gap-6">
                    {/* ⚠️ THE CHILD'S NAME IS THE CARD'S HEADING. P8 makes these
                        settings per-child, and a parent with two children needs
                        to know which one they are about to opt out of recording
                        — the name was `dash-md` bold in a column of `dash-sm`
                        text, which is not a heading. */}
                    <p className="text-dash-lg font-extrabold text-ink">
                      {c.childName}
                    </p>

                    {/* P4 — layer 2 of the recording chain. */}
                    <div className="-mx-3">
                      <CheckboxRow
                        checked={c.recordingOptOut}
                        onChange={(recordingOptOut) =>
                          void saveChildPreferences(
                            c.playerId,
                            { recordingOptOut },
                            auth,
                          ).then(reload)
                        }
                        hint={t.recordingOptOutHelp}
                      >
                        {t.recordingOptOut}
                      </CheckboxRow>
                    </div>

                    {/* P8 — per-child message kinds, showing which are inherited
                        so a parent knows which of the two screens they changed. */}
                    <fieldset className="-mx-3">
                      <legend className="mb-1 px-3 text-dash-xs font-bold text-muted">
                        {t.messageKinds}
                      </legend>
                      {c.kinds.map((k) => (
                        <CheckboxRow
                          key={k.kind}
                          checked={k.enabled}
                          onChange={(enabled) =>
                            void saveChildPreferences(
                              c.playerId,
                              { kinds: [{ kind: k.kind, enabled }] },
                              auth,
                            ).then(reload)
                          }
                          /* Which of the two screens set this. Without it a
                             parent cannot tell a per-child choice from the
                             account default it is currently following. */
                          hint={k.inherited ? t.inherited : undefined}
                        >
                          {kindLabel(k.kind, lang)}
                        </CheckboxRow>
                      ))}
                    </fieldset>

                    <div>
                      <DashButton variant="secondary" onClick={onOpenConsent}>
                        <PrivacyIcon size={16} />
                        {t.consentTitle}
                      </DashButton>
                    </div>
                  </div>
                </Panel>
              )}
            </Surface>
          </div>
        </>
      )}

      {/*
        ⚠️ LAST, AND IN A SECTION OF ITS OWN, BECAUSE IT IS NOT SAVED WITH THE
        REST. Everything above is on the account or on the child and travels with
        the parent to any phone they sign in from; this is a browser key on this
        device. Putting it inside "For this account" would be a promise the store
        cannot keep — and unlike those, it takes effect the instant it is ticked
        rather than after a round trip.
      */}
      <SectionHead>{t.deviceSettings}</SectionHead>
      <Panel>
        <div className="-mx-3">
          <CheckboxRow
            checked={reduceMotion}
            onChange={(on) => {
              setReduceMotion(on);
              setReduceMotionSetting(on);
            }}
            hint={t.reduceMotionHelp}
          >
            {t.reduceMotion}
          </CheckboxRow>
        </div>
      </Panel>

      {saved && (
        <p className="mt-6">
          <StatusPill tone="success" icon={<CheckIcon size={14} />}>
            {t.saved}
          </StatusPill>
        </p>
      )}
    </section>
  );
}

/**
 * Minutes past midnight, as an `<input type="time">`.
 *
 * ⚠️ THE STORED VALUE IS IST MINUTES, AND THE INPUT IS A WALL CLOCK. They are
 * the same thing by construction — `prefs.prisma` stores minutes precisely so
 * there is no timezone in the column — so this converts between the two and
 * nothing here reads a clock.
 */
function MinuteInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (minutes: number | null) => void;
}) {
  const asTime =
    value === null
      ? ""
      : `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;

  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-dash text-dash-xs font-bold text-muted">
        {label}
      </span>
      {/* ⚠️ THE KIT'S FIELD, NOT `border px-2 py-1`. A native time input at 15px
          with hairline padding is a control a parent taps at twice on a phone —
          and this one decides when the product is allowed to message them. */}
      <input
        type="time"
        className={`${FIELD} w-[8.75rem] text-dash-sm`}
        value={asTime}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          const [h, m] = raw.split(":").map(Number);
          onChange(h * 60 + m);
        }}
      />
    </label>
  );
}

function kindLabel(kind: string, lang: Lang): string {
  const t = PARENT_COPY[lang];
  switch (kind) {
    case "WEEKLY_REPORT":
      return t.kindWeeklyReport;
    case "ACHIEVEMENT":
      return t.kindAchievement;
    case "REMINDER":
      return t.kindReminder;
    default:
      return t.kindSystem;
  }
}

/**
 * One child's games, on the parent tier.
 *
 * 🔴 THE SAME PANEL AND THE SAME SERVER COMPUTATION THE TEACHER SEES, AND THAT
 * IS DELIBERATE RATHER THAN CONVENIENT. A parent and a teacher reading two
 * different numbers about the same child is the argument that starts at the
 * school gate; `architecture.md` §12's *"define each metric once, scope-filter it
 * per tier"* is what stops it, and one shared panel is the client half of that.
 *
 * 🔴 AND IT DOES NOT BREAK THIS TIER'S POSTURE. `ParentService` refuses an
 * "overall %" tile and the charts here carry *"no target and no comparison with
 * other children"* in their own copy. Nothing on this panel compares: every
 * figure is this child's own rounds, their own right answers, the level they
 * themselves reached. It is the reason `/players/:playerId/games` admits a parent
 * and `/players/:playerId/strengths` does not.
 */
export function ChildGamesPanel({
  playerId,
  lang,
  auth,
}: {
  playerId: string;
  lang: Lang;
  auth?: ParentAuth;
}) {
  const t = PARENT_COPY[lang];
  const resource = useResource<GameStatsResponse>(
    (signal) => getPlayerGames(playerId, { ...auth, signal }),
    ["parent-child-games", playerId],
  );

  return (
    <DataSurface
      state={resource.state}
      skeletonRows={5}
      errorMessage={t.loadFailed}
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : (
        <GamesPanel
          games={resource.data.games}
          summary={resource.data.summary}
          lang={lang}
          copy={{
            ...GAMES_PANEL_COPY[lang],
            title: t.gamesTitle,
            hint: t.gamesHint,
          }}
        />
      )}
    </DataSurface>
  );
}
